// piano-frontend/src/pages/AdminPage.js
import React, { useEffect, useRef, useState } from "react";
import {
  Box, Typography, Paper, Tabs, Tab,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, Stack, Divider,
  InputLabel, FormControl, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Toolbar, InputAdornment, Alert, TableSortLabel, Chip, Tooltip,
  FormControlLabel, Checkbox, useMediaQuery, Menu, ListItemIcon, ListItemText, Fab, Collapse,
  Snackbar, CircularProgress, Avatar, Badge, Popover
} from "@mui/material";
import { Person, School, Add, Edit, Delete, CalendarToday, NotificationsActive, NotificationsOff, LockReset, PersonOff, PersonAdd, SwapHoriz, Payment, GridView, Block, LockOpen, History, MoreVert, Tune, Close, EventBusy, Undo, ConfirmationNumber, Update, SwapVert, ArrowUpward, ArrowDownward, Check, EventNote, MusicNote, Visibility, VisibilityOff } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import { alpha } from "@mui/material/styles";
import api from "../api/axios";
import ScheduleSlotView from "../components/ScheduleSlotView";
import ScheduleConflictPreview from "../components/ScheduleConflictPreview";
import UnassignedResultDialog from "../components/UnassignedResultDialog";
import MakeupBookingDialog from "../components/MakeupBookingDialog";
import SchedulePlanner from "../components/SchedulePlanner";
import MakeupDecisionDialog from "../components/MakeupDecisionDialog";
import ScheduleCarryoverDialog from "../components/ScheduleCarryoverDialog";
import DateField from "../components/DateField";
import AdminSidebar from "./admin/AdminSidebar";
import AdminDashboard from "./admin/AdminDashboard";
import OperationsSettings from "./admin/OperationsSettings";
import { computeTransitionWeek, fmtDateLocal } from "../utils/transitionWeek";
import { matchesAnySearch } from "../utils/hangul";
import { stripSeconds } from "../utils/timeFormat";
import { useDarkMode } from '../App';

const DAY_OPTIONS = [
  { value: "MON", label: "월요일" },
  { value: "TUE", label: "화요일" },
  { value: "WED", label: "수요일" },
  { value: "THU", label: "목요일" },
  { value: "FRI", label: "금요일" },
  { value: "SAT", label: "토요일" },
];
const TIME_OPTIONS = [
  "10:00", "10:30", "11:00", "11:30","12:00", "12:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
  "17:00", "17:30", "18:00", "18:30", "19:00", "19:30", "20:00", "20:30",
  "21:00", "21:30", "22:00"
];

// 티켓 이력 사유 코드 → 라벨/아이콘 매핑 (색상은 변동 부호로 파생)
const TICKET_REASON_CONFIG = {
  LESSON_RESERVE: { label: "레슨 예약", Icon: EventBusy },
  LESSON_CANCEL: { label: "레슨 취소", Icon: Undo },
  HOLIDAY_REFUND: { label: "휴일 반환", Icon: Undo },
  WORKDAY_RESTORE: { label: "업무일 차감", Icon: EventBusy },
  REQUEST_RESERVE: { label: "레슨 신청", Icon: EventBusy },
  REQUEST_APPROVE_CANCEL: { label: "취소 승인", Icon: Undo },
  REQUEST_REJECT: { label: "요청 반려", Icon: Block },
  REQUEST_CANCEL: { label: "신청 취소", Icon: Undo },
  ADMIN_SET: { label: "관리자 설정", Icon: Tune },
  ADMIN_GRANT: { label: "티켓 부여", Icon: ConfirmationNumber },
  PAYMENT: { label: "결제 처리", Icon: Payment },
  BATCH_EXPIRY_UPDATE: { label: "유효기간 변경", Icon: Update },
  DEACTIVATE: { label: "비활성화", Icon: PersonOff },
  MAKEUP_ISSUE: { label: "보강 티켓 발행", Icon: ConfirmationNumber },
  MAKEUP_RECLAIM: { label: "보강 티켓 회수", Icon: Undo },
};

// 티켓 이력 행의 날짜 그룹 라벨 (오늘/어제/M월 D일 (요일))
const _KO_DAYS = ["일", "월", "화", "수", "목", "금", "토"];
function ticketDateLabel(dateStr) {
  const d = new Date(dateStr);
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (diff === 0) return "오늘";
  if (diff === 1) return "어제";
  return `${d.getMonth() + 1}월 ${d.getDate()}일 (${_KO_DAYS[d.getDay()]})`;
}
function ticketTimeLabel(dateStr) {
  return new Date(dateStr).toLocaleTimeString("ko-KR", { hour: "numeric", minute: "2-digit" });
}

// 툴바 액션 버튼: PC(≥600px)에서는 아이콘 + 하단 라벨, 모바일에서는 아이콘 + 툴팁
function ActionButton({ title, icon, color = "primary", onClick, disabled = false }) {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("sm"));

  if (isDesktop) {
    return (
      <Button
        color={color}
        variant="text"
        onClick={onClick}
        disabled={disabled}
        sx={{
          flexDirection: "column",
          minWidth: 60,
          maxWidth: 160,
          px: 1,
          py: 0.5,
          gap: 0.25,
          lineHeight: 1.15,
        }}
      >
        {icon}
        <Box
          component="span"
          sx={{ fontSize: "0.68rem", fontWeight: 600, textAlign: "center", whiteSpace: "nowrap" }}
        >
          {title}
        </Box>
      </Button>
    );
  }

  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          color={color}
          onClick={onClick}
          disabled={disabled}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function AdminPage({ active = true }) {
  const { darkMode } = useDarkMode();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [tab, setTab] = useState(0);
  const [section, setSection] = useState("dashboard"); // PC 좌측 내비 섹션
  // 운영 설정 keep-alive: 한 번 방문하면 언마운트하지 않고 숨겨서, 다른 섹션에 다녀와도 편집 중이던 상태를 유지한다
  const [opsMounted, setOpsMounted] = useState(false);
  useEffect(() => {
    if (!isMobile && section === "operations") setOpsMounted(true);
  }, [isMobile, section]);
  // 편집 폼은 모바일 전체화면 또는 PC 우측 사이드 패널(≤600px)에서만 렌더되므로, 넓은 표 대신 항상 좁은(세로 카드) 레이아웃 사용
  const formNarrow = true;
  // 모바일 카드 액션 메뉴(⋮)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  // 모바일 정렬 메뉴(PC 표 헤더의 정렬 기능을 모바일 카드 목록에서 대체)
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  // 모바일 필터 접이식
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null); // PC 패널 열림 시 필터 Popover 앵커
  const [teachers, setTeachers] = useState([]);
  const [members, setMembers] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [lessonSchedules, setLessonSchedules] = useState([]);
  const [teacherSchedules, setTeacherSchedules] = useState([]);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState("");
  const [showTempPw, setShowTempPw] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);
  const [usernameChanged, setUsernameChanged] = useState(false);
  const [scheduleApplyFrom, setScheduleApplyFrom] = useState("");
  // 정렬 상태는 탭(선생님/회원)별로 보관 — 다른 탭이나 섹션에 다녀와도 회원 탭에서 정한 정렬이 유지된다
  const [sortState, setSortState] = useState({
    0: { order: 'asc', orderBy: 'username' },
    1: { order: 'asc', orderBy: 'username' },
  });
  const { order, orderBy } = sortState[tab] || { order: 'asc', orderBy: 'username' };
  const [dialogTab, setDialogTab] = useState(0);
  const [scheduleOverrides, setScheduleOverrides] = useState([]);
  const [overrideForm, setOverrideForm] = useState({
    date: "",
    type: "OFF",
    work_start: "09:00",
    work_end: "18:00",
    reason: ""
  });
  const [filters, setFilters] = useState({
    member_type: "",
    teacher_id: "",
    has_push_subscription: "",
    practice_room_access: "",
    is_active: "true"  // 기본적으로 활성 사용자만 표시
  });
  
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferData, setTransferData] = useState({
    oldTeacherId: null,
    oldTeacherName: "",
    candidates: [],
    fromDate: new Date().toISOString().slice(0, 10),
    step: 1
  });
  const [lessonDetailDialog, setLessonDetailDialog] = useState({
    open: false,
    studentName: "",
    oldTeacherName: "",
    lessons: []
  });
  
  // 충돌 시뮬레이션 결과
  const [simulationResult, setSimulationResult] = useState(null);

  // 고정제 자동 예약 결과(미배정/보강 티켓) 다이얼로그 + 보강 레슨 잡기
  const [unassignedDialog, setUnassignedDialog] = useState({ open: false, result: null, memberId: null, memberName: "", title: "", queue: [], seq: null });
  const [makeupDialog, setMakeupDialog] = useState({ open: false, member: null, initialDate: null, marks: [] });

  // 이미 사용된 보강 티켓 처리 결정 다이얼로그 (회원 수정 저장 시 409 응답)
  const [makeupDecisionDialog, setMakeupDecisionDialog] = useState({ open: false, decision: null, pending: null, submitting: false });
  // 스케줄제 진입 시 남은 티켓 이월 결정 (서버 409 requiresScheduleCarryover → 결정 실어 재저장)
  const [carryoverDialog, setCarryoverDialog] = useState({ open: false, carryover: null, pending: null, submitting: false });

  // 연습실 예약 제한 다이얼로그
  const [blockPracticeDialog, setBlockPracticeDialog] = useState({
    open: false,
    memberId: null,
    memberName: "",
    hours: 24
  });

  // 티켓 이력 다이얼로그
  const [ticketHistoryDialog, setTicketHistoryDialog] = useState({ open: false, memberName: "", ticket: 0, reschedule: 0 });
  const [ticketHistory, setTicketHistory] = useState([]);
  const [ticketHistoryFilter, setTicketHistoryFilter] = useState("all"); // all | in | out

  // 레슨 이력 (회원이 실제로 받은 레슨) — 시작일은 기본 비움(무제한), 스크롤로 과거를 이어서 불러온다
  const LESSON_HISTORY_PAGE_SIZE = 50;
  const [lessonHistoryDialog, setLessonHistoryDialog] = useState({ open: false, memberId: null, memberName: "", from: "", to: "" });
  const [lessonHistory, setLessonHistory] = useState([]);
  const [lessonHistoryTeacher, setLessonHistoryTeacher] = useState("");
  const [lessonHistoryTeacherOptions, setLessonHistoryTeacherOptions] = useState([]);
  const [lessonHistoryTotal, setLessonHistoryTotal] = useState(0);
  const [lessonHistoryHasMore, setLessonHistoryHasMore] = useState(false);
  const [lessonHistoryLoading, setLessonHistoryLoading] = useState(false); // 최초/필터 변경 조회 (전체 스피너)
  const [lessonHistoryLoadingMore, setLessonHistoryLoadingMore] = useState(false); // 스크롤 추가 조회 (하단 스피너)
  const lessonHistoryReqRef = useRef(0); // 늦게 도착한 이전 조회 응답 무시용
  const lessonHistoryLoadingMoreRef = useRef(false); // 추가 조회 중복 트리거 방지 (state는 비동기라 즉시성 보장 안 됨)

  // 티켓 유효기간 (스케줄제 회원 편집 다이얼로그 내 인라인)
  const [ticketBatches, setTicketBatches] = useState([]);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [editingExpiresAt, setEditingExpiresAt] = useState("");
  const [grantForm, setGrantForm] = useState({ amount: 4, expires_at: "", description: "" });

  // 스케줄제 결제 처리 시 유효기간 입력 다이얼로그
  const [paymentDialog, setPaymentDialog] = useState({ open: false, memberId: null, memberName: "", lessonsPerWeek: 1 });
  const [paymentExpiresAt, setPaymentExpiresAt] = useState("");

  // 초기 목록 로딩 상태 (스피너 표시용)
  const [initialLoading, setInitialLoading] = useState(true);

  // 스낵바(토스트) — window.alert 대체
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnackbar = (message, severity = "success") => {
    const cleaned = typeof message === "string" ? message.replace(/^(✅|❌|⚠️|ℹ️)\s*/, "") : message;
    setSnackbar({ open: true, message: cleaned, severity });
  };
  const closeSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar(s => ({ ...s, open: false }));
  };

  // 확인/안내 다이얼로그 — window.confirm 대체 (Promise 기반)
  const [confirmState, setConfirmState] = useState({
    open: false, title: "", message: "", confirmText: "확인", cancelText: "취소",
    color: "primary", severity: null, hideCancel: false, resolve: null,
  });
  const askConfirm = (opts) => new Promise(resolve => {
    setConfirmState({
      open: true, title: "", message: "", confirmText: "확인", cancelText: "취소",
      color: "primary", severity: null, hideCancel: false, ...opts, resolve,
    });
  });
  const closeConfirm = (result) => {
    confirmState.resolve?.(result);
    setConfirmState(s => ({ ...s, open: false, resolve: null }));
  };
  // 안내 전용(확인 버튼만) — 상세 결과 표시에 사용
  const showAlert = (opts) => askConfirm({ hideCancel: true, ...opts });

  // keep-alive로 숨겨질 때: 모달·메뉴는 포털로 body에 렌더되어 display:none으로 안 가려진다.
  // 편집·판단이 들어가는 다이얼로그(회원 수정, 이관, 보강 예약/결정, 미배정 결과)는 닫지 않고
  // open={... && active}로 숨겼다가 복귀 시 복원하고, 일시적 오버레이(메뉴·확인·조회·결제 등)만 닫는다.
  useEffect(() => {
    if (active) return;
    setMenuAnchorEl(null);
    setFilterMenuAnchor(null);
    setResetDialogOpen(false);
    setLessonDetailDialog((d) => (d.open ? { ...d, open: false } : d));
    setBlockPracticeDialog((d) => (d.open ? { ...d, open: false } : d));
    setTicketHistoryDialog((d) => (d.open ? { ...d, open: false } : d));
    setLessonHistoryDialog((d) => (d.open ? { ...d, open: false } : d));
    setPaymentDialog((d) => (d.open ? { ...d, open: false } : d));
    setSnackbar((s) => (s.open ? { ...s, open: false } : s));
    setConfirmState((s) => {
      if (!s.open) return s;
      s.resolve?.(false);
      return { ...s, open: false, resolve: null };
    });
  }, [active]);

  const resetFilters = () => {
    setFilters({
      member_type: "",
      teacher_id: "",
      has_push_subscription: "",
      practice_room_access: "",
      is_active: "true"  // 초기화 시에도 활성 사용자만 표시
    });
  };

  const filteredMembers = members
    // 초성 검색 지원 — 'ㅎㄱㄷ' 로 '홍길동' 이 잡힌다.
    .filter(item => matchesAnySearch([item.name, item.username], search))
    .filter(item => {
      if (filters.member_type && item.member_type !== filters.member_type) return false;
      if (filters.teacher_id && item.teacher?.id !== Number(filters.teacher_id)) return false;
      if (filters.has_push_subscription !== "" && item.has_push_subscription !== (filters.has_push_subscription === "true")) return false;
      if (filters.practice_room_access !== "" && item.practice_room_access !== (filters.practice_room_access === "true")) return false;
      if (filters.is_active !== "" && item.is_active !== (filters.is_active === "true")) return false;
      return true;
    });

  function fetchAll() {
    const p1 = api.get("/teachers/admin/detail").then(res => {
      setTeachers(res.data);
      setTeacherOptions(res.data);
    }).catch(() => setTeacherOptions([]));

    const p2 = api.get("/members").then(res => setMembers(res.data)).catch(() => {});

    Promise.allSettled([p1, p2]).finally(() => setInitialLoading(false));
  }

  useEffect(fetchAll, []);

  // 편집 패널이 닫히면 필터 Popover 앵커도 정리 (재오픈 시 stale 노드 방지)
  useEffect(() => { if (!openDialog) setFilterMenuAnchor(null); }, [openDialog]);

  // 지난 변동도 열람할 수 있게 전체 이력 조회 (지난 항목은 목록에서 열람 전용)
  async function fetchScheduleOverrides(teacherId) {
    try {
      const res = await api.get(`/teachers/${teacherId}/schedule-overrides`, {
        params: { include_past: true }
      });
      setScheduleOverrides(res.data);
    } catch (err) {
      console.error('변동 스케줄 조회 실패:', err);
      setScheduleOverrides([]);
    }
  }

  const overrideTodayStr = fmtDateLocal(new Date());
  const upcomingOverrides = scheduleOverrides.filter(o => o.date >= overrideTodayStr);
  const pastOverrides = scheduleOverrides
    .filter(o => o.date < overrideTodayStr)
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  // 결제 처리 대상 여부 — ⋮ 메뉴와 모바일 카드 버튼이 같은 조건을 쓴다
  const canProcessPayment = (item) =>
    !!item && (item.member_type === 'FIXED' || item.member_type === 'SCHEDULE') && item.is_active;

  // 대상 회원을 인자로 받는다. 모바일 카드의 결제 버튼은 행을 선택하지 않고 바로 누르므로
  // selected에 기대면 직전에 고른 회원이 결제되는 사고가 난다.
  async function handleProcessPayment(target) {
    const memberId = target?.id ?? selected?.id;
    if (!memberId) return;
    const member = members.find(m => m.id === memberId);
    const isSchedule = member?.member_type === 'SCHEDULE';
    const lessonsPerWeek = member?.lessons_per_week || 1;

    if (isSchedule) {
      // 스케줄제: 유효기간 입력 다이얼로그 오픈
      setPaymentExpiresAt("");
      setPaymentDialog({ open: true, memberId, memberName: member?.name || "", lessonsPerWeek });
      return;
    }

    if (!(await askConfirm({
      title: "결제 처리",
      message:
        `${member?.name || member?.username || ""} 회원의 결제 처리를 진행하시겠습니까?\n\n` +
        `• 레슨 시작일: ${member?.lesson_start_date || "기존 날짜"} → +1개월\n` +
        "• 정식 예약 1개월 생성\n" +
        "• 임시 예약 1개월 생성 (총 2개월)",
      confirmText: "결제 처리",
    }))) return;

    try {
      setLoading(true);
      const res = await api.post(`/members/${memberId}/process-payment`);
      showSnackbar(res.data.message, "success");
      setSelected(null);
      fetchAll();
    } catch (e) {
      const errorMessage = e?.response?.data?.message || '결제 처리 실패';
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleProcessPaymentConfirm() {
    if (!paymentExpiresAt) {
      showSnackbar('유효기간을 입력해주세요.', 'warning');
      return;
    }
    const { memberId } = paymentDialog;
    try {
      setLoading(true);
      const res = await api.post(`/members/${memberId}/process-payment`, { expires_at: paymentExpiresAt });
      showSnackbar(res.data.message, "success");
      setPaymentDialog({ open: false, memberId: null, memberName: "", lessonsPerWeek: 1 });
      setSelected(null);
      fetchAll();
    } catch (e) {
      showSnackbar(e?.response?.data?.message || '오류 발생', "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleGrantTickets() {
    if (!grantForm.expires_at) { showSnackbar('유효기간을 입력해주세요.', 'warning'); return; }
    if (!grantForm.amount || grantForm.amount < 1) { showSnackbar('티켓 수는 1 이상이어야 합니다.', 'warning'); return; }
    try {
      setLoading(true);
      const res = await api.post(`/members/${selected.id}/grant-tickets`, grantForm);
      showSnackbar(res.data.message, "success");
      setGrantForm({ amount: 4, expires_at: "", description: "" });
      await fetchTicketBatches(selected.id);
      fetchAll();
    } catch (e) {
      showSnackbar(e?.response?.data?.message || '오류 발생', "error");
    } finally {
      setLoading(false);
    }
  }

  async function fetchTicketBatches(memberId) {
    const res = await api.get(`/members/${memberId}/ticket-batches`);
    setTicketBatches(res.data);
  }


  // 레슨 이력 조회 — append=false면 필터 변경으로 인한 첫 페이지 재조회(목록 전체 교체),
  // append=true면 스크롤에 의한 다음 페이지 추가(목록 뒤에 이어붙임).
  // 필터를 연달아 바꾸면 요청이 겹칠 수 있어, 마지막 요청의 응답만 반영한다.
  async function fetchLessonHistoryPage(append) {
    const { memberId, from, to } = lessonHistoryDialog;
    if (!memberId) return;
    if (append) {
      if (!lessonHistoryHasMore || lessonHistoryLoadingMoreRef.current) return;
      lessonHistoryLoadingMoreRef.current = true;
      setLessonHistoryLoadingMore(true);
    } else {
      setLessonHistoryLoading(true);
    }
    const reqId = ++lessonHistoryReqRef.current;
    try {
      const res = await api.get(`/members/${memberId}/lessons`, {
        params: {
          from: from || undefined,
          to,
          teacher_id: lessonHistoryTeacher || undefined,
          offset: append ? lessonHistory.length : 0,
          limit: LESSON_HISTORY_PAGE_SIZE,
        },
      });
      if (reqId !== lessonHistoryReqRef.current) return;
      const newRows = res.data?.lessons || [];
      setLessonHistory((prev) => (append ? [...prev, ...newRows] : newRows));
      setLessonHistoryTotal(res.data?.total ?? 0);
      setLessonHistoryHasMore(!!res.data?.hasMore);
      if (res.data?.teachers) setLessonHistoryTeacherOptions(res.data.teachers);
    } catch (e) {
      if (reqId !== lessonHistoryReqRef.current) return;
      if (!append) { setLessonHistory([]); setLessonHistoryTotal(0); setLessonHistoryHasMore(false); }
      showSnackbar(append ? '레슨 이력 추가 조회 실패' : '레슨 이력 조회 실패', "error");
    } finally {
      if (append) { lessonHistoryLoadingMoreRef.current = false; setLessonHistoryLoadingMore(false); }
      else if (reqId === lessonHistoryReqRef.current) setLessonHistoryLoading(false);
    }
  }

  // 다이얼로그가 열려 있는 동안 회원/기간/선생님 필터가 바뀌면 첫 페이지부터 다시 조회
  useEffect(() => {
    if (!lessonHistoryDialog.open || !lessonHistoryDialog.memberId) return;
    setLessonHistory([]);
    setLessonHistoryHasMore(false);
    fetchLessonHistoryPage(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonHistoryDialog.open, lessonHistoryDialog.memberId, lessonHistoryDialog.from, lessonHistoryDialog.to, lessonHistoryTeacher]);

  function openLessonHistory(item) {
    const to = dayjs().format("YYYY-MM-DD");
    setLessonHistoryTeacher("");
    setLessonHistoryTeacherOptions([]);
    setLessonHistoryDialog({
      open: true,
      memberId: item.id,
      memberName: item.name || item.username,
      from: "",
      to,
    });
  }

  // 시작일(비우면 무제한)/종료일 변경: 역전 입력은 나머지 한쪽을 끌어당겨 항상 from <= to 를 유지
  function changeLessonHistoryRange(field, value) {
    setLessonHistoryDialog((d) => {
      if (field === "to") {
        const to = value || dayjs().format("YYYY-MM-DD");
        return { ...d, to, from: d.from && d.from > to ? to : d.from };
      }
      const from = value || ""; // 빈 값 = 시작일 제한 없음
      return { ...d, from, to: from && from > d.to ? from : d.to };
    });
  }

  // 목록 스크롤이 바닥 근처에 닿으면 다음 페이지를 이어서 불러온다
  function handleLessonHistoryScroll(e) {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      fetchLessonHistoryPage(true);
    }
  }

  async function handleUpdateBatchExpiry(memberId, batchId) {
    if (!editingExpiresAt) { showSnackbar('유효기간을 입력해주세요.', 'warning'); return; }
    try {
      setLoading(true);
      const res = await api.put(`/members/${memberId}/ticket-batches/${batchId}`, { expires_at: editingExpiresAt });
      showSnackbar(res.data.message, "success");
      setEditingBatchId(null);
      setEditingExpiresAt("");
      await fetchTicketBatches(memberId);
      fetchAll();
    } catch (e) {
      showSnackbar(e?.response?.data?.message || '오류 발생', "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteBatch(memberId, batchId, remaining) {
    const msg = remaining > 0
      ? `이 배치를 삭제하면 잔여 티켓 ${remaining}장이 차감됩니다.\n정말 삭제하시겠습니까?`
      : '이 배치를 삭제하시겠습니까?';
    if (!(await askConfirm({ title: "티켓 배치 삭제", message: msg, confirmText: "삭제", color: "error" }))) return;
    try {
      setLoading(true);
      const res = await api.delete(`/members/${memberId}/ticket-batches/${batchId}`);
      showSnackbar(res.data.message, "success");
      await fetchTicketBatches(memberId);
      fetchAll();
    } catch (e) {
      showSnackbar(e?.response?.data?.message || '오류 발생', "error");
    } finally {
      setLoading(false);
    }
  }

  // 연습실 예약 제한 설정
  async function handleBlockPractice() {
    try {
      setLoading(true);
      const res = await api.post(`/members/${blockPracticeDialog.memberId}/block-practice`, {
        hours: blockPracticeDialog.hours
      });
      showSnackbar(res.data.message, "success");
      setBlockPracticeDialog({ open: false, memberId: null, memberName: "", hours: 24 });
      // 선택 상태 업데이트 (버튼 상태 즉시 반영)
      if (selected && selected.id === blockPracticeDialog.memberId) {
        const blockedUntil = new Date(Date.now() + blockPracticeDialog.hours * 60 * 60 * 1000).toISOString();
        setSelected(prev => ({ ...prev, practice_blocked_until: blockedUntil }));
      }
      fetchAll();
    } catch (e) {
      const errorMessage = e?.response?.data?.message || '연습실 예약 제한 실패';
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  }

  // 연습실 예약 제한 해제
  async function handleUnblockPractice(memberId, memberName) {
    if (!(await askConfirm({ title: "예약 제한 해제", message: `${memberName}님의 연습실 예약 제한을 해제하시겠습니까?`, confirmText: "해제" }))) return;

    try {
      setLoading(true);
      const res = await api.delete(`/members/${memberId}/block-practice`);
      showSnackbar(res.data.message, "success");
      // 선택 상태 업데이트 (버튼 상태 즉시 반영)
      if (selected && selected.id === memberId) {
        setSelected(prev => ({ ...prev, practice_blocked_until: null }));
      }
      fetchAll();
    } catch (e) {
      const errorMessage = e?.response?.data?.message || '연습실 예약 제한 해제 실패';
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  }

  async function openTransferDialog(teacher) {
    try {
      const res = await api.get(`/teachers/${teacher.id}/transfer-candidates`);
      const activeTeachers = teachers.filter(t => t.is_active && t.id !== teacher.id);
      const defaultNewTeacherId = activeTeachers[0]?.id || "";
      
      setTransferData({
        oldTeacherId: teacher.id,
        oldTeacherName: teacher.name,
        candidates: res.data.map(c => ({
          ...c,
          selected: true,
          new_teacher_id: defaultNewTeacherId
        })),
        fromDate: new Date().toISOString().slice(0, 10),
        step: 1
      });
      setTransferDialogOpen(true);
    } catch (err) {
      showSnackbar('이관 대상 조회 실패: ' + (err?.response?.data?.message || err.message), "error");
    }
  }

  function updateTransferCandidate(memberId, field, value) {
    setTransferData(prev => ({
      ...prev,
      candidates: prev.candidates.map(c => 
        c.id === memberId ? { ...c, [field]: value } : c
      )
    }));
  }

  async function handleTransferStudents() {
    const selectedCandidates = transferData.candidates.filter(c => c.selected);
    
    if (selectedCandidates.length === 0) {
      showSnackbar('이관할 학생을 선택해주세요.', 'warning');
      return;
    }

    const transfers = selectedCandidates.map(c => ({
      member_id: c.id,
      new_teacher_id: c.new_teacher_id
    }));

    try {
      setLoading(true);
      const res = await api.post('/teachers/transfer-students', { transfers });
      showSnackbar(res.data.message, "success");
      await fetchAll();
      setTransferData(prev => ({ ...prev, step: 2 }));
    } catch (err) {
      showSnackbar('학생 이관 실패: ' + (err?.response?.data?.message || err.message), "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleTransferReservations() {
    const selectedCandidates = transferData.candidates.filter(c => c.selected);
    const newTeacherIds = [...new Set(selectedCandidates.map(c => c.new_teacher_id))];

    try {
      setLoading(true);
      let totalUpdated = 0;
      
      for (const newTeacherId of newTeacherIds) {
        const memberIds = selectedCandidates
          .filter(c => c.new_teacher_id === newTeacherId)
          .map(c => c.id);
        
        const res = await api.post('/teachers/transfer-reservations', {
          old_teacher_id: transferData.oldTeacherId,
          new_teacher_id: newTeacherId,
          from_date: transferData.fromDate,
          member_ids: memberIds
        });
        totalUpdated += res.data.updatedReservations;
      }
      
      showSnackbar(`총 ${totalUpdated}건의 레슨이 이관되었습니다.`, "success");
      await fetchAll();
      setTransferData(prev => ({ ...prev, step: 3 }));
    } catch (err) {
      showSnackbar('레슨 이관 실패: ' + (err?.response?.data?.message || err.message), "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleDeactivateTeacher() {
    if (!(await askConfirm({ title: "선생님 비활성화", message: `${transferData.oldTeacherName} 선생님을 비활성화하시겠습니까?`, confirmText: "비활성화", color: "warning" }))) return;

    try {
      await api.put(`/teachers/${transferData.oldTeacherId}/deactivate`);
      showSnackbar('선생님이 비활성화되었습니다.', "success");
      setTransferDialogOpen(false);
      await fetchAll();
    } catch (err) {
      showSnackbar('비활성화 실패: ' + (err?.response?.data?.message || err.message), "error");
    }
  }

  async function viewStudentLessons(student) {
    try {
      setLoading(true);
      const today = new Date().toISOString().slice(0, 10);
      const res = await api.get(`/lessons?start_date=${today}&end_date=2099-12-31`);
      
      const studentLessons = res.data
        .filter(lesson => 
          lesson.user_id === student.id && 
          lesson.teacher_id === transferData.oldTeacherId
        )
        .sort((a, b) => {
          if (a.date !== b.date) return a.date.localeCompare(b.date);
          return a.start_time.localeCompare(b.start_time);
        });

      setLessonDetailDialog({
        open: true,
        studentName: student.name,
        oldTeacherName: transferData.oldTeacherName,
        lessons: studentLessons
      });
    } catch (err) {
      showSnackbar('레슨 정보 조회 실패: ' + (err?.response?.data?.message || err.message), "error");
    } finally {
      setLoading(false);
    }
  }

  function openEdit(obj, isTeacher) {
    setError("");
    setUsernameChanged(false);
    setDialogTab(0);
    setSimulationResult(null);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setScheduleApplyFrom(tomorrow.toISOString().slice(0, 10));
    // lesson_schedules: 전환 주 검사(handleSave)가 기존 요일과 새 요일을 비교할 때 사용
    setSelected(obj ? { id: obj.id, isTeacher, member_type: obj.member_type, originalUsername: obj.username, lesson_schedules: obj.lesson_schedules || [] } : null);

    if (isTeacher && obj?.schedules) {
      setForm({ ...obj });
      setTeacherSchedules(obj.schedules.map(s => ({
        day: s.day,
        start: s.start.slice(0, 5),
        end: s.end.slice(0, 5)
      })));
      fetchScheduleOverrides(obj.id);
    } else if (isTeacher && !obj) {
      setForm({});
      setTeacherSchedules([]);
      setScheduleOverrides([]);
    } else if (!isTeacher && obj) {
      setForm({
        ...obj,
        lesson_start_date: obj.lesson_start_date || ""
      });
      if (obj.lesson_schedules && obj.lesson_schedules.length > 0) {
        setLessonSchedules(obj.lesson_schedules.map(s => ({
          day: s.lesson_day,
          start: s.lesson_start.slice(0, 5),
          end: s.lesson_end.slice(0, 5),
          teacher_id: s.teacher_id
        })));
      } else {
        setLessonSchedules([]);
      }
      if (obj.member_type === 'SCHEDULE') {
        setTicketBatches([]);
        setEditingBatchId(null);
        setGrantForm({ amount: 4, expires_at: "", description: "" });
        fetchTicketBatches(obj.id).catch(() => {});
      } else {
        setTicketBatches([]);
      }
    } else {
      setForm({});
      setLessonSchedules([]);
    }
    setOpenDialog(true);
  }

  function addTeacherSchedule() {
    setTeacherSchedules([...teacherSchedules, { day: "MON", start: "09:00", end: "18:00" }]);
  }
  function updateTeacherSchedule(index, field, value) {
    const updated = [...teacherSchedules];
    updated[index][field] = value;
    setTeacherSchedules(updated);
  }
  function deleteTeacherSchedule(index) {
    setTeacherSchedules(teacherSchedules.filter((_, i) => i !== index));
  }

  function addLessonSchedule() {
    const activeTeachers = teacherOptions.filter(t => t.is_active);
    setLessonSchedules([...lessonSchedules, { 
      day: "MON", 
      start: "14:00", 
      end: "14:30", 
      teacher_id: form.teacher_id || activeTeachers[0]?.id || "" 
    }]);
  }
  function updateLessonSchedule(index, field, value) {
    const updated = [...lessonSchedules];
    updated[index][field] = value;
    if (field === "start") {
      // TIME_OPTIONS가 30분 간격 연속 목록이므로 다음 항목이 곧 시작+30분
      const next = TIME_OPTIONS[TIME_OPTIONS.indexOf(value) + 1];
      if (next) updated[index].end = next;
    }
    setLessonSchedules(updated);
  }
  function deleteLessonSchedule(index) {
    setLessonSchedules(lessonSchedules.filter((_, i) => i !== index));
  }

  async function handleAddOverride() {
    if (!overrideForm.date) {
      showSnackbar("날짜를 선택해주세요.", 'warning');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/teachers/${selected.id}/schedule-overrides`, overrideForm);
      showSnackbar("변동 스케줄이 등록되었습니다.", "success");
      setOverrideForm({
        date: "",
        type: "OFF",
        work_start: "09:00",
        work_end: "18:00",
        reason: ""
      });
      fetchScheduleOverrides(selected.id);
    } catch (e) {
      const errorMessage = e?.response?.data?.message || '변동 스케줄 등록 실패';
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteOverride(overrideId) {
    if (!(await askConfirm({ title: "변동 스케줄 삭제", message: "정말 이 변동 스케줄을 삭제하시겠습니까?", confirmText: "삭제", color: "error" }))) return;

    try {
      await api.delete(`/teachers/schedule-overrides/${overrideId}`);
      showSnackbar("변동 스케줄이 삭제되었습니다.", "success");
      fetchScheduleOverrides(selected.id);
    } catch (e) {
      showSnackbar("삭제 실패 : 변동 스케줄에 예약된 사용자가 있습니다.", "error");
    }
  }

  // 미배정/보강 티켓/회수/상쇄/보존 등 관리자에게 알릴 내용이 있는지
  function hasUnassignedContent(ua) {
    return !!ua && (
      (ua.skips && ua.skips.length > 0) ||
      (ua.ticketsIssued || 0) > 0 ||
      (ua.reclaimed || 0) > 0 ||
      (ua.movedResolved || 0) > 0 ||
      (ua.coveredRemoved || 0) > 0 ||
      (ua.unreclaimable && ua.unreclaimable.length > 0) ||
      (ua.practiceWarnings && ua.practiceWarnings.length > 0)
    );
  }

  // 고정제 자동 예약 결과: 알릴 내용이 있으면 결과 다이얼로그, 없으면 스낵바
  function showMemberSaveResult(res, { memberId, memberName, isUpdate }) {
    const ua = res?.data?.unassigned;
    if (hasUnassignedContent(ua)) {
      setUnassignedDialog({
        open: true,
        result: ua,
        memberId,
        memberName,
        title: isUpdate ? "회원 수정 완료" : "회원 등록 완료",
        queue: [],
        seq: null,
      });
    } else {
      showSnackbar(isUpdate ? "회원 정보가 수정되었습니다." : "회원이 등록되었습니다.", "success");
    }
  }

  // 플래너 일괄 적용 결과: 회원별로 알릴 내용이 있으면 결과 다이얼로그를 차례로 표시
  // (여기서도 발행된 보강 티켓을 "지금 보강 레슨 잡기"로 바로 사용할 수 있게)
  const handlePlannerApplied = (data) => {
    fetchAll();
    const items = (data?.results || [])
      .filter((r) => r.success && hasUnassignedContent(r.unassigned))
      .map((r) => ({
        result: r.unassigned,
        memberId: r.member_id,
        memberName: r.memberName,
        title: "스케줄 변경 완료",
      }));
    if (items.length > 0) {
      // seq: "회원 N명 중 M번째" 표기 — 관리자가 몇 명을 확인/보강 잡기 해야 하는지 알 수 있게
      setUnassignedDialog({ open: true, ...items[0], queue: items.slice(1), seq: { current: 1, total: items.length } });
    } else {
      showSnackbar(data?.message || "스케줄이 변경되었습니다.", "success");
    }
  };

  // 결정 다이얼로그 확정 → makeup_decision을 실어 저장 재시도.
  // 다이얼로그를 닫지 않고 '적용 중' 상태로 유지하다가, 응답 도착과 동시에 결과 화면으로
  // 전환한다 (모달이 완전히 꺼졌다가 다시 뜨는 공백 제거).
  async function handleMakeupDecisionConfirm(decisionChoice) {
    const pending = makeupDecisionDialog.pending;
    if (!pending) return;
    setMakeupDecisionDialog((prev) => ({ ...prev, submitting: true }));
    setLoading(true);
    try {
      const res = await api.put(`/members/${pending.id}`, { ...pending.saveForm, makeup_decision: decisionChoice });
      setMakeupDecisionDialog({ open: false, decision: null, pending: null, submitting: false });
      showMemberSaveResult(res, { memberId: pending.id, memberName: pending.memberName, isUpdate: true });
      setOpenDialog(false);
      setLessonSchedules([]);
      setSimulationResult(null);
      fetchAll();
    } catch (e) {
      const errorMessage = e?.response?.data?.message || "저장에 실패했습니다.";
      showSnackbar(errorMessage, "error");
      if (e?.response?.status === 409 && e?.response?.data?.requiresMakeupDecision) {
        // 선택 검증 실패 등 → 최신 데이터로 다시 결정 요청 (다이얼로그 유지)
        setMakeupDecisionDialog({ open: true, decision: e.response.data.decision, pending, submitting: false });
      } else {
        setMakeupDecisionDialog({ open: false, decision: null, pending: null, submitting: false });
      }
    } finally {
      setLoading(false);
    }
  }

  // 이월 결정 확정 → schedule_carryover를 실어 저장 재시도 (pending.id 없으면 신규 등록)
  async function handleCarryoverConfirm(scheduleCarryover) {
    const pending = carryoverDialog.pending;
    if (!pending) return;
    setCarryoverDialog((prev) => ({ ...prev, submitting: true }));
    setLoading(true);
    try {
      const saveForm = { ...pending.saveForm, schedule_carryover: scheduleCarryover };
      const res = pending.id
        ? await api.put(`/members/${pending.id}`, saveForm)
        : await api.post("/members", saveForm);
      setCarryoverDialog({ open: false, carryover: null, pending: null, submitting: false });
      showMemberSaveResult(res, {
        memberId: pending.id ?? res?.data?.user?.id,
        memberName: pending.memberName,
        isUpdate: !!pending.id,
      });
      setOpenDialog(false);
      setLessonSchedules([]);
      setSimulationResult(null);
      fetchAll();
    } catch (e) {
      // 유효기간 검증 실패 등 → 다이얼로그 유지하고 다시 선택
      showSnackbar(e?.response?.data?.message || "저장에 실패했습니다.", "error");
      setCarryoverDialog((prev) => ({ ...prev, submitting: false }));
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setError("");
    
    // 고정제 회원 + 차단 수준 충돌(다른 회원과 반복 슬롯 겹침): 저장해도 전 회차가
    // 자리 없음(티켓 변환) 처리되는 무의미한 저장이므로 저장을 막고 플래너로 유도한다
    if (tab === 1 && form.member_type === "FIXED" && simulationResult && !simulationResult.canProceed) {
      const goPlanner = await askConfirm({
        title: "고정 스케줄 겹침",
        severity: "error",
        message:
          "다른 회원과 고정 스케줄이 정확히 겹칩니다.\n" +
          "이대로 저장하면 모든 회차가 자리 없음으로 처리되어 의미가 없습니다.\n" +
          "스케줄 플래너에서 고정 스케줄을 변경해주세요.",
        confirmText: "플래너 열기",
        color: "primary",
      });
      if (goPlanner) handleSection("planner");
      return;
    }

    // 요일이 이동하는 변경 + 적용 시작일이 주 중간이면 전환 주가 평소 주당 횟수와
    // 달라질 수 있다(초과: 지난 기존 요일 수업 + 새 요일 수업 공존 / 부족: 기존 요일은
    // 삭제되고 새 요일은 이미 지남) → 진행 전 안내
    if (tab === 1 && form.member_type === "FIXED" && selected && lessonSchedules.length > 0) {
      const oldDays = (selected.lesson_schedules || []).map((s) => s.lesson_day);
      if (oldDays.length > 0) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const applyFrom = scheduleApplyFrom || fmtDateLocal(tomorrow);
        const tw = computeTransitionWeek(applyFrom, oldDays, lessonSchedules.map((s) => s.day));
        if (tw && tw.total !== lessonSchedules.length) {
          const diff = tw.total - lessonSchedules.length;
          const diffLabel = diff > 0 ? `${diff}회 초과` : `${-diff}회 부족`;
          const proceed = await askConfirm({
            title: "전환 주 수업 횟수 안내",
            severity: "warning",
            message:
              `적용 시작일(${applyFrom})이 주 중간이라 전환 주(${tw.weekStartStr}~${tw.weekEndStr}) 수업이 평소와 달라집니다.\n\n` +
              `전환 주: 이미 지난 기존 요일 수업 ${tw.oldKept}회 + 새 요일 수업 ${tw.newCreated}회 = 총 ${tw.total}회` +
              ` (평소 주 ${lessonSchedules.length}회 — ${diffLabel})\n\n` +
              `평소대로 하려면 취소 후 적용 시작일을 다음 주 월요일로 지정해주세요.`,
            confirmText: "이대로 진행",
            color: "warning",
          });
          if (!proceed) return;
        }
      }
    }

    setLoading(true);

    try {
      if (tab === 0) {
        const saveForm = { ...form, schedules: teacherSchedules };

        if (selected) {
          try {
            await api.put(`/teachers/${selected.id}`, saveForm);
          } catch (err) {
            // 새 근무시간 밖의 회원 고정 스케줄/레슨 예약 충돌 → 목록 확인 후 무시하고 저장 가능
            if (err?.response?.status === 409 && err?.response?.data?.requiresForce) {
              const conflicts = err.response.data.conflicts || [];
              const lines = conflicts.slice(0, 10).map(c =>
                c.type === 'SCHEDULE'
                  ? `• [고정 스케줄] ${c.member_name} — ${c.day} ${c.time}`
                  : `• [레슨 예약] ${c.member_name} — ${c.date} ${c.time}`
              );
              if (conflicts.length > 10) lines.push(`• 외 ${conflicts.length - 10}건`);
              const proceed = await askConfirm({
                title: "근무 스케줄 충돌 경고",
                severity: "warning",
                message:
                  `변경하려는 근무 시간 밖에 있는 회원 스케줄/예약이 ${conflicts.length}건 있습니다.\n\n` +
                  lines.join("\n") +
                  `\n\n이대로 저장하면 해당 스케줄/예약은 근무 시간 밖에 그대로 남습니다.\n계속하시겠습니까?`,
                confirmText: "무시하고 저장",
                color: "warning",
              });
              if (!proceed) return;
              await api.put(`/teachers/${selected.id}`, { ...saveForm, force: true });
            } else {
              throw err;
            }
          }
          showSnackbar("선생님 정보가 수정되었습니다.", "success");
        } else {
          await api.post("/teachers", saveForm);
          showSnackbar("선생님이 등록되었습니다.", "success");
        }
        
        setOpenDialog(false);
        setTeacherSchedules([]);
        fetchAll();
      } else {
        const saveForm = {
          ...form,
          lesson_schedules: form.member_type === "FIXED" ? lessonSchedules : [],
          schedule_apply_from: scheduleApplyFrom
        };

        let res;
        if (selected) {
          try {
            res = await api.put(`/members/${selected.id}`, saveForm);
          } catch (err) {
            // 스케줄제 전환 + 남은 티켓 → 이월/소멸 관리자 결정 필요
            if (err?.response?.status === 409 && err?.response?.data?.requiresScheduleCarryover) {
              setCarryoverDialog({
                open: true,
                carryover: err.response.data.carryover,
                pending: { id: selected.id, saveForm, memberName: form.name },
                submitting: false,
              });
              return; // 결정 후 재시도 (수정 다이얼로그는 열린 채 유지)
            }
            // 이미 사용된 보강 티켓 → 관리자 결정(수동 예약 제거 / 새 일정 일부 제외) 필요
            if (err?.response?.status === 409 && err?.response?.data?.requiresMakeupDecision) {
              setMakeupDecisionDialog({
                open: true,
                decision: err.response.data.decision,
                pending: { id: selected.id, saveForm, memberName: form.name },
                submitting: false,
              });
              return; // 결정 후 재시도 (수정 다이얼로그는 열린 채 유지)
            }
            // 서버측 재검증에서 반복 슬롯 겹침 감지 → 저장 거부 + 플래너 유도
            if (err?.response?.status === 409 && err?.response?.data?.scheduleConflicts) {
              const goPlanner = await askConfirm({
                title: "고정 스케줄 겹침",
                severity: "error",
                message:
                  "다른 회원과 고정 스케줄이 정확히 겹칩니다.\n" +
                  "스케줄 플래너에서 고정 스케줄을 변경해주세요.",
                confirmText: "플래너 열기",
                color: "primary",
              });
              if (goPlanner) handleSection("planner");
              return;
            }
            throw err;
          }
        } else {
          try {
            res = await api.post("/members", saveForm);
          } catch (err) {
            // 스케줄제 등록 + 초기 티켓 → 이월/소멸 관리자 결정 필요
            if (err?.response?.status === 409 && err?.response?.data?.requiresScheduleCarryover) {
              setCarryoverDialog({
                open: true,
                carryover: err.response.data.carryover,
                pending: { id: null, saveForm, memberName: form.name },
                submitting: false,
              });
              return;
            }
            throw err;
          }
        }

        showMemberSaveResult(res, {
          memberId: selected ? selected.id : res?.data?.user?.id,
          memberName: form.name,
          isUpdate: !!selected,
        });

        setOpenDialog(false);
        setLessonSchedules([]);
        setSimulationResult(null);
        fetchAll();
      }
    } catch (e) {
      console.error('저장 에러:', e);
      const errorMessage = e?.response?.data?.message || '저장에 실패했습니다.';
      setError(errorMessage);
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  }

  // 같은 항목을 다시 누르면 오름/내림 토글, 다른 항목이면 오름차순으로 시작
  const handleRequestSort = (property) => {
    setSortState(prev => {
      const cur = prev[tab] || { order: 'asc', orderBy: 'username' };
      const isAsc = cur.orderBy === property && cur.order === 'asc';
      return { ...prev, [tab]: { order: isAsc ? 'desc' : 'asc', orderBy: property } };
    });
  };

  // 모바일 정렬 메뉴 항목 (PC 표 헤더의 정렬 가능 컬럼과 동일)
  const MEMBER_SORT_OPTIONS = [
    { key: 'username', label: '이름' },
    { key: 'member_type', label: '유형' },
    { key: 'teacher', label: '선생님' },
    { key: 'ticket', label: '티켓' },
    { key: 'lesson_start_date', label: '레슨시작일' },
  ];
  const TEACHER_SORT_OPTIONS = [
    { key: 'username', label: '이름' },
    { key: 'is_active', label: '상태' },
  ];

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    if (orderBy === 'teacher') {
      aVal = a.teacher?.name || '';
      bVal = b.teacher?.name || '';
    }

    if (orderBy === 'lesson_start_date') {
      aVal = a.lesson_start_date || '';
      bVal = b.lesson_start_date || '';
      if (!aVal && !bVal) return 0;
      if (!aVal) return -1;
      if (!bVal) return 1;
      if (bVal < aVal) return -1;
      if (bVal > aVal) return 1;
      return 0;
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return bVal - aVal;
    }

    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
  };

  // 요일 라벨 (칩 표시용)
  const DAY_LABEL = { MON: "월", TUE: "화", WED: "수", THU: "목", FRI: "금", SAT: "토" };

  // 선택된 행에 대한 액션 목록 — PC 툴바와 모바일 카드 ⋮ 메뉴가 공유
  function getRowActions(item, isTeacher) {
    if (!item) return [];
    const actions = [];
    // 수정 (공통)
    actions.push({
      key: "edit", label: "수정", color: "primary", icon: <Edit />,
      onClick: () => openEdit(item, isTeacher),
    });

    if (isTeacher) {
      if (item.is_active && item.student_count > 0) {
        actions.push({
          key: "transfer", label: "학생 이관", color: "secondary", icon: <SwapHoriz />,
          onClick: () => openTransferDialog(item),
        });
      }
      if (item.is_active) {
        actions.push({
          key: "deactivate", label: "비활성화", color: "warning", icon: <PersonOff />,
          onClick: async () => {
            try {
              setLoading(true);
              await api.put(`/teachers/${item.id}/deactivate`);
              showSnackbar('선생님이 비활성화되었습니다.', "success");
              setSelected(null);
              fetchAll();
            } catch (e) {
              const err = e?.response?.data;
              if (err?.studentCount > 0 || err?.futureReservations > 0) {
                showAlert({
                  title: "비활성화 불가",
                  severity: "warning",
                  message: `담당 학생: ${err.studentCount}명\n잔여 레슨: ${err.futureReservations}건\n\n먼저 학생 이관을 진행해주세요.`,
                });
              } else {
                showSnackbar(err?.message || '비활성화 실패', "error");
              }
            } finally {
              setLoading(false);
            }
          },
        });
      } else {
        actions.push({
          key: "activate", label: "활성화", color: "success", icon: <PersonAdd />,
          onClick: async () => {
            try {
              setLoading(true);
              await api.put(`/teachers/${item.id}/activate`);
              showSnackbar('선생님이 활성화되었습니다.', "success");
              setSelected(null);
              fetchAll();
            } catch (e) {
              showSnackbar(e?.response?.data?.message || '활성화 실패', "error");
            } finally {
              setLoading(false);
            }
          },
        });
      }
      actions.push({
        key: "delete", label: "삭제", color: "error", icon: <Delete />,
        onClick: async () => {
          if (!(await askConfirm({ title: "선생님 삭제", message: "정말 해당 선생님을 삭제하시겠습니까?", confirmText: "삭제", color: "error" }))) return;
          try {
            setLoading(true);
            await api.delete(`/teachers/${item.id}`);
            showSnackbar("선생님이 삭제되었습니다.", "success");
            setSelected(null);
            fetchAll();
          } catch (e) {
            const errorMessage = e?.response?.data?.message || "삭제에 실패했습니다.";
            showSnackbar(errorMessage, "error");
          } finally {
            setLoading(false);
          }
        },
      });
      return actions;
    }

    // 회원
    if (canProcessPayment(item)) {
      actions.push({
        key: "payment", label: "결제 완료", color: "success", icon: <Payment />,
        onClick: () => handleProcessPayment(item), disabled: loading,
      });
    }
    if (item.is_active) {
      actions.push({
        key: "deactivate", label: "비활성화", color: "warning", icon: <PersonOff />,
        onClick: async () => {
          if (!(await askConfirm({
            title: "회원 비활성화",
            severity: "warning",
            message:
              "회원을 비활성화하시겠습니까?\n\n" +
              "⚠️ 미래의 모든 예약이 삭제됩니다.\n" +
              "• 정식 예약(결제 완료): 티켓 복구\n" +
              "• 임시 예약(미결제): 티켓 복구 안 됨",
            confirmText: "비활성화",
            color: "warning",
          }))) return;
          try {
            setLoading(true);
            const res = await api.put(`/members/${item.id}/deactivate`);
            const msg = [
              res.data.message,
              '',
              `📊 삭제된 레슨: ${res.data.deletedLessons}개`,
              `  - 정식 예약: ${res.data.paidLessons}개`,
              `  - 임시 예약: ${res.data.provisionalLessons}개`,
              '',
              `🎫 복구된 티켓: ${res.data.restoredTickets}개`,
            ].join('\n');
            showAlert({ title: "회원 비활성화 완료", severity: "success", message: msg });
            setSelected(null);
            fetchAll();
          } catch (e) {
            showSnackbar(e?.response?.data?.message || '오류 발생', "error");
          } finally {
            setLoading(false);
          }
        },
      });
    } else {
      actions.push({
        key: "activate", label: "활성화", color: "success", icon: <PersonAdd />,
        onClick: async () => {
          if (!(await askConfirm({ title: "회원 활성화", message: "회원을 활성화하시겠습니까?", confirmText: "활성화" }))) return;
          try {
            setLoading(true);
            await api.put(`/members/${item.id}/activate`);
            showSnackbar('회원이 활성화되었습니다.', "success");
            setSelected(null);
            fetchAll();
          } catch (e) {
            showSnackbar(e?.response?.data?.message || '오류 발생', "error");
          } finally {
            setLoading(false);
          }
        },
      });
    }
    actions.push({
      key: "delete", label: "삭제", color: "error", icon: <Delete />,
      onClick: async () => {
        // 서버가 409(requireConfirmation)를 주는 건 미래 예약이 있을 때뿐이라,
        // 예약 없는 회원은 이 1차 확인이 없으면 클릭 즉시 삭제된다.
        if (!(await askConfirm({
          title: "회원 삭제",
          severity: "warning",
          message: `${item.name}(${item.username}) 회원을 삭제하시겠습니까? 오늘 이후의 레슨·연습실 예약과 고정 레슨 스케줄이 함께 삭제되며 되돌릴 수 없습니다.`,
          confirmText: "삭제",
          color: "error",
        }))) return;
        try {
          setLoading(true);
          const response = await api.delete(`/members/${item.id}`);
          showSnackbar(response.data.message, "success");
          setSelected(null);
          fetchAll();
        } catch (e) {
          const responseData = e?.response?.data;
          if (e?.response?.status === 409 && responseData?.requireConfirmation) {
            const confirmDelete = await askConfirm({ title: "회원 삭제 확인", severity: "warning", message: responseData.message, confirmText: "삭제", color: "error" });
            if (confirmDelete) {
              try {
                const forceResponse = await api.delete(`/members/${item.id}?force=true`);
                showSnackbar(forceResponse.data.message, "success");
                setSelected(null);
                fetchAll();
              } catch (forceError) {
                const errorMessage = forceError?.response?.data?.message || "삭제에 실패했습니다.";
                showSnackbar(errorMessage, "error");
              }
            }
          } else {
            const errorMessage = responseData?.message || "삭제에 실패했습니다.";
            showSnackbar(errorMessage, "error");
          }
        } finally {
          setLoading(false);
        }
      },
    });
    actions.push({
      key: "resetPw", label: "비밀번호 초기화", color: "warning", icon: <LockReset />,
      onClick: () => {
        setResetPassword("");
        setResetDialogOpen(true);
      },
    });
    actions.push({
      key: "ticketHistory", label: "티켓 이력", color: "info", icon: <History />,
      onClick: async () => {
        try {
          const res = await api.get(`/members/${item.id}/ticket-history`);
          setTicketHistory(res.data);
          setTicketHistoryFilter("all");
          setTicketHistoryDialog({
            open: true,
            memberName: item.name || item.username,
            ticket: item.ticket ?? 0,
            reschedule: item.reschedule_ticket_count ?? 0,
          });
        } catch (e) {
          showSnackbar('티켓 이력 조회 실패', "error");
        }
      },
    });
    actions.push({
      key: "lessonHistory", label: "레슨 이력", color: "info", icon: <EventNote />,
      onClick: () => openLessonHistory(item),
    });
    const isBlocked = item.practice_blocked_until && new Date(item.practice_blocked_until) > new Date();
    if (isBlocked) {
      actions.push({
        key: "unblock", label: "연습실 예약 제한 해제", color: "success", icon: <LockOpen />,
        onClick: () => handleUnblockPractice(item.id, item.name || item.username),
      });
    } else {
      actions.push({
        key: "block", label: "연습실 예약 제한", color: "error", icon: <Block />,
        onClick: () => setBlockPracticeDialog({
          open: true,
          memberId: item.id,
          memberName: item.name || item.username,
          hours: 24,
        }),
      });
    }
    return actions;
  }

  // 모바일 카드의 ⋮ 버튼: 해당 행을 선택 상태로 만든 뒤 메뉴 오픈
  const openRowMenu = (e, item) => {
    setSelected({
      id: item.id,
      isTeacher: tab === 0,
      member_type: item.member_type,
      originalUsername: item.username,
      name: item.name || item.username,
      practice_blocked_until: item.practice_blocked_until,
    });
    setMenuItem(item);
    setMenuAnchorEl(e.currentTarget);
  };
  const closeRowMenu = () => setMenuAnchorEl(null);

  // 모바일 목록 카드 (선생님/회원 공용)
  const renderRowCard = (item) => {
    const isTeacher = tab === 0;
    const blocked = item.practice_blocked_until && new Date(item.practice_blocked_until) > new Date();
    return (
      <Paper
        key={item.id}
        variant="outlined"
        sx={{ p: 1.25, borderRadius: 3, opacity: item.is_active === false ? 0.6 : 1 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>{item.username}</Typography>
          <Box sx={{ flex: 1 }} />
          {blocked && (
            <Chip icon={<Block sx={{ fontSize: "0.9rem !important" }} />} label="예약제한" size="small" color="error" sx={{ fontSize: "0.7rem" }} />
          )}
          <Chip
            label={item.is_active ? "활성" : "비활성"}
            size="small"
            color={item.is_active ? "success" : "default"}
          />
          <IconButton size="small" onClick={(e) => openRowMenu(e, item)} aria-label="작업 메뉴">
            <MoreVert />
          </IconButton>
        </Box>

        {isTeacher ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              담당 학생 {item.student_count || 0}명
            </Typography>
            {item.schedules && item.schedules.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                {item.schedules.map((s, idx) => (
                  <Chip
                    key={idx}
                    label={`${DAY_LABEL[s.day]} ${s.start.slice(0, 5)}-${s.end.slice(0, 5)}`}
                    size="small"
                    sx={{ bgcolor: darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd", color: "primary.main", fontWeight: 500, fontSize: "0.72rem" }}
                  />
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ mt: 0.5, display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Box sx={{ display: "flex" }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 48, flexShrink: 0 }}>유형</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                : {item.member_type === "COUNT" ? "횟수제" : item.member_type === "FIXED" ? "고정제" : "스케줄제"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex" }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 48, flexShrink: 0 }}>선생님</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>: {item.teacher?.name || "미배정"}</Typography>
            </Box>
            <Box sx={{ display: "flex" }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 48, flexShrink: 0 }}>티켓</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                : {item.ticket ?? 0}장{(item.reschedule_ticket_count ?? 0) > 0 ? ` (일반 ${Math.max(0, (item.ticket ?? 0) - (item.reschedule_ticket_count ?? 0))} · 변경권 ${item.reschedule_ticket_count})` : ""}
              </Typography>
            </Box>
            {/* 고정제만 레슨시작일을 사용 — 결제 처리 시 한 달 밀리므로 결제 시점 판단 기준이 된다 */}
            {item.member_type === "FIXED" && (
              <Box sx={{ display: "flex" }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 48, flexShrink: 0 }}>시작일</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>: {item.lesson_start_date || "미설정"}</Typography>
              </Box>
            )}
          </Box>
          {/* 결제 완료는 관리자가 매일 반복하는 주 작업이라 ⋮ 메뉴 밖으로 꺼내 둔다.
              정보 영역이 헤더보다 길어 ⋮ 아래가 비므로 여기 놓으면 카드 높이는 그대로다. */}
          {canProcessPayment(item) && (
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<Payment sx={{ fontSize: "1rem !important" }} />}
              disabled={loading}
              onClick={() => handleProcessPayment(item)}
              sx={{ flexShrink: 0, fontSize: "0.72rem", py: 0.25, px: 1, minWidth: 0, whiteSpace: "nowrap" }}
            >
              결제 완료
            </Button>
          )}
          </Box>
        )}
      </Paper>
    );
  };

  // 회원 검색창 (PC: 필터와 한 줄 / 모바일: 상단 고정)
  const searchField = (
    <TextField
      size="small"
      variant="outlined"
      placeholder="이름 또는 초성으로 검색"
      value={search}
      autoComplete="off"
      onChange={(e) => setSearch(e.target.value)}
      // 320px 기기에서 placeholder가 잘리지 않도록 돋보기 아이콘과 좌우 여백을 줄인다.
      sx={{
        width: { xs: "100%", sm: 240 },
        "& .MuiOutlinedInput-root": { backgroundColor: darkMode ? "grey.800" : "white", boxShadow: 1, pl: 1.5 },
        "& .MuiOutlinedInput-input": { pr: 1.5 },
        "& .MuiInputAdornment-positionStart": { mr: 0.75 },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="action" fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );

  // 회원 필터 셀렉트 묶음 (PC 인라인 / 모바일 접이식 공용)
  const filterControls = (
    <>
      <FormControl size="small" sx={{ minWidth: 100 }}>
        <InputLabel>유형</InputLabel>
        <Select value={filters.member_type} label="유형" onChange={e => setFilters({ ...filters, member_type: e.target.value })}>
          <MenuItem value="">전체</MenuItem>
          <MenuItem value="COUNT">횟수제</MenuItem>
          <MenuItem value="FIXED">고정제</MenuItem>
          <MenuItem value="SCHEDULE">스케줄제</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>선생님</InputLabel>
        <Select value={filters.teacher_id} label="선생님" onChange={e => setFilters({ ...filters, teacher_id: e.target.value })}>
          <MenuItem value="">전체</MenuItem>
          {teacherOptions.map(t => (
            <MenuItem key={t.id} value={t.id}>{t.name} {!t.is_active && "(비활성)"}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 100 }}>
        <InputLabel>알림</InputLabel>
        <Select value={filters.has_push_subscription} label="알림" onChange={e => setFilters({ ...filters, has_push_subscription: e.target.value })}>
          <MenuItem value="">전체</MenuItem>
          <MenuItem value="true">활성</MenuItem>
          <MenuItem value="false">비활성</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 100 }}>
        <InputLabel>연습실</InputLabel>
        <Select value={filters.practice_room_access} label="연습실" onChange={e => setFilters({ ...filters, practice_room_access: e.target.value })}>
          <MenuItem value="">전체</MenuItem>
          <MenuItem value="true">사용</MenuItem>
          <MenuItem value="false">미사용</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 100 }}>
        <InputLabel>상태</InputLabel>
        <Select value={filters.is_active} label="상태" onChange={e => setFilters({ ...filters, is_active: e.target.value })}>
          <MenuItem value="">전체</MenuItem>
          <MenuItem value="true">활성</MenuItem>
          <MenuItem value="false">비활성</MenuItem>
        </Select>
      </FormControl>

      {Object.values(filters).some(v => v !== "") && (
        <Button size="small" onClick={resetFilters}>필터 초기화</Button>
      )}
    </>
  );

  // PC 좌측 내비 섹션 전환 (섹션 ↔ tab 동기화)
  const handleSection = (s) => {
    setSection(s);
    if (s === "teachers") setTab(0);
    else if (s === "members") setTab(1);
    else if (s === "schedule") setTab(2);
    else if (s === "planner") setTab(3);
    setSelected(null);
    setOpenDialog(false);
    setSimulationResult(null);
  };

  // 대시보드에서 회원 클릭 → 회원 섹션 이동 + 해당 회원 선택
  const handleSelectMemberFromDashboard = (memberId) => {
    setSection("members");
    setTab(1);
    const m = members.find((x) => x.id === memberId);
    setSelected(
      m
        ? { id: m.id, isTeacher: false, member_type: m.member_type, originalUsername: m.username, name: m.name || m.username, practice_blocked_until: m.practice_blocked_until }
        : { id: memberId, isTeacher: false }
    );
  };

  // PC: 상세 패널이 열리면 목록 표는 폭을 줄이고 일부 컬럼을 감춘다(형태는 표 그대로 유지)
  const panelOpen = !isMobile && openDialog && section !== "dashboard" && section !== "planner";

  return (
    <Box sx={{ pt: { xs: 2, sm: 0 }, pb: { xs: 8, sm: 0 }, px: { sm: 0 }, maxWidth: { xs: 1200, sm: "none" }, mx: "auto", height: { sm: "calc(100vh - 112px)" }, display: { sm: "flex" }, flexDirection: { sm: "column" }, minHeight: 0 }}>
      <Box sx={{ bgcolor: "background.paper", overflow: { sm: "hidden" }, flex: { sm: 1 }, minHeight: 0, display: { sm: "flex" }, flexDirection: { sm: "row" } }}>
        {!isMobile && (
          <AdminSidebar section={section} onChange={handleSection} darkMode={darkMode} pendingCount={0} />
        )}

        <Box sx={{ flex: { sm: 1 }, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", overflow: { sm: "hidden" }, pr: { sm: panelOpen ? "620px" : 0 } }}>
          {!isMobile && opsMounted && (
            <Box sx={{ flex: 1, minHeight: 0, display: section === "operations" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
              <OperationsSettings />
            </Box>
          )}
          {!isMobile && section === "dashboard" ? (
            <AdminDashboard onSelectMember={handleSelectMemberFromDashboard} onGotoSection={handleSection} />
          ) : !isMobile && section === "planner" ? (
            <SchedulePlanner onApplied={handlePlannerApplied} />
          ) : !isMobile && section === "operations" ? null : (
          <>
        {isMobile && (
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setSelected(null);
            setOpenDialog(false);
            setSimulationResult(null);
          }}
          centered={!isMobile}
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            "& .MuiTab-root": { fontWeight: 600, minWidth: 0, minHeight: { xs: 56, sm: 54 }, fontSize: { xs: "0.8rem", sm: "0.875rem" }, px: { xs: 1, sm: 2 } },
            minHeight: { sm: 54 },
            "& .Mui-selected": { color: "primary.main" },
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Tab icon={<School />} label={isMobile ? "선생님" : "선생님 관리"} />
          <Tab icon={<Person />} label={isMobile ? "회원" : "회원 관리"} />
          <Tab icon={<GridView />} label={isMobile ? "스케줄" : "스케줄 현황"} />
          <Tab icon={<SwapHoriz />} label="플래너" />
        </Tabs>
        )}

        {/* 회원 관리 탭 필터 */}
        {tab === 1 && (
          <Box sx={{ px: 2, pb: 1, flexShrink: 0 }}>
            {isMobile ? (
              <>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 2, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>{searchField}</Box>
                  <Button size="small" variant="outlined" startIcon={<Tune />} onClick={() => setFiltersOpen(o => !o)} sx={{ flexShrink: 0 }}>
                    필터
                  </Button>
                </Box>
                <Collapse in={filtersOpen}>
                  <Stack direction="column" spacing={1} sx={{ mb: 1, "& .MuiFormControl-root": { width: "100%" } }}>
                    {filterControls}
                  </Stack>
                </Collapse>
              </>
            ) : panelOpen ? (
              // 패널 열림(좁은 폭): 검색창 + "필터" 버튼(Popover)으로 접어 줄바꿈 방지
              <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: "center" }}>
                {searchField}
                <Badge
                  badgeContent={Object.values(filters).filter(v => v !== "").length}
                  color="primary"
                  sx={{ flexShrink: 0 }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Tune />}
                    onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                  >
                    필터
                  </Button>
                </Badge>
                <Popover
                  open={Boolean(filterMenuAnchor)}
                  anchorEl={filterMenuAnchor}
                  onClose={() => setFilterMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <Stack direction="column" spacing={1.5} sx={{ p: 2, width: 240, "& .MuiFormControl-root": { width: "100%" } }}>
                    {filterControls}
                  </Stack>
                </Popover>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: "wrap", alignItems: "center", "& .MuiFormControl-root": { width: "auto" } }}>
                {searchField}
                {filterControls}
              </Stack>
            )}
          </Box>
        )}

        {/* 스케줄 현황 탭 */}
        {tab === 2 && (
          <Box sx={{ p: { xs: 1, sm: 2 }, flex: { sm: 1 }, minHeight: 0, overflow: { sm: "auto" } }}>
            <ScheduleSlotView
              teachers={teacherOptions}
              onMemberClick={(memberId) => {
                const member = members.find(m => m.id === memberId);
                if (member) {
                  setSection("members");
                  setTab(1);
                  setTimeout(() => {
                    openEdit(member, false);
                  }, 100);
                }
              }}
            />
          </Box>
        )}

        {/* 스케줄 플래너 탭 (모바일: 전체 페이지 인라인) */}
        {isMobile && tab === 3 && (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <SchedulePlanner onApplied={handlePlannerApplied} />
          </Box>
        )}

        {/* 선생님/회원 목록 (탭 0, 1) */}
        {tab !== 2 && tab !== 3 && (
          <>
            {initialLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8, flex: { sm: 1 }, minHeight: 0 }}>
                <CircularProgress />
              </Box>
            ) : isMobile ? (
              <Box sx={{ pb: 1 }}>
                <Box sx={{ px: 2, py: 1, display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography fontWeight={600}>{tab === 0 ? "선생님 목록" : "회원 목록"}</Typography>
                  <Typography variant="body2" color="text.secondary">총 {(tab === 0 ? teachers : filteredMembers).length}명</Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    startIcon={<SwapVert />}
                    endIcon={order === 'asc' ? <ArrowUpward sx={{ fontSize: "0.9rem !important" }} /> : <ArrowDownward sx={{ fontSize: "0.9rem !important" }} />}
                    onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                    sx={{ flexShrink: 0, color: "text.secondary", fontWeight: 500, px: 0.75 }}
                  >
                    {(tab === 0 ? TEACHER_SORT_OPTIONS : MEMBER_SORT_OPTIONS).find(o => o.key === orderBy)?.label || "정렬"}
                  </Button>
                </Box>
                <Box sx={{ px: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                  {(tab === 0 ? [...teachers] : filteredMembers).sort(getComparator(order, orderBy)).map(item => renderRowCard(item))}
                  {(tab === 0 ? teachers : filteredMembers).length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                      표시할 {tab === 0 ? "선생님" : "회원"}이 없습니다.
                    </Typography>
                  )}
                </Box>
                <Fab
                  color="primary"
                  aria-label={tab === 0 ? "선생님 등록" : "회원 등록"}
                  onClick={() => openEdit(null, tab === 0)}
                  sx={{ position: "fixed", bottom: "calc(72px + env(safe-area-inset-bottom, 0px))", right: 16, zIndex: 1200 }}
                >
                  <Add />
                </Fab>
              </Box>
            ) : (
              <>
            <Toolbar sx={{ display: "flex", justifyContent: "space-between", backgroundColor: darkMode ? "grey.800" : "#f5f5f5", borderBottom: "1px solid", borderColor: "divider", flexShrink: 0 }}>
              <Typography fontWeight={600}>
                {tab === 0 ? "선생님 목록" : "회원 목록"}
                <Box component="span" sx={{ ml: 1, fontWeight: 400, fontSize: "0.85rem", color: "text.secondary" }}>
                  (총 {(tab === 0 ? teachers : filteredMembers).length}명)
                </Box>
              </Typography>

              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap", justifyContent: "flex-end", rowGap: 0.5 }}>
                {selected?.id && (() => {
                  const fullItem = (tab === 0 ? teachers : members).find(x => x.id === selected.id);
                  if (!fullItem) return null;
                  return getRowActions(fullItem, tab === 0).map(a => (
                    <ActionButton
                      key={a.key}
                      title={a.label}
                      color={a.color}
                      icon={a.icon}
                      onClick={a.onClick}
                      disabled={a.disabled || loading}
                    />
                  ));
                })()}

                <ActionButton
                  title={tab === 0 ? "선생님 등록" : "회원 등록"}
                  color="primary"
                  icon={<Add />}
                  onClick={() => openEdit(null, tab === 0)}
                />
              </Stack>
            </Toolbar>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <TableContainer>
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    minWidth: tab === 1 ? (panelOpen ? 580 : 1000) : 600,
                    // 정렬 아이콘이 비활성 상태에서도 폭을 차지해 헤더 라벨이 왼쪽으로 밀리는 것을 방지 (정렬 중인 컬럼만 화살표 표시)
                    "& .MuiTableSortLabel-icon": { display: "none" },
                    "& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon": { display: "inline-block" },
                    // 정렬 화살표가 붙어도 헤더 라벨이 글자 단위로 줄바꿈되지 않도록
                    "& .MuiTableHead-root .MuiTableCell-root": { whiteSpace: "nowrap" },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {tab === 0 ? (
                        <>
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 84 }} align="center">
                            <TableSortLabel
                              active={orderBy === 'username'}
                              direction={orderBy === 'username' ? order : 'asc'}
                              onClick={() => handleRequestSort('username')}
                            >
                              이름
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 90 }} align="center">
                            <TableSortLabel
                              active={orderBy === 'is_active'}
                              direction={orderBy === 'is_active' ? order : 'asc'}
                              onClick={() => handleRequestSort('is_active')}
                            >
                              상태
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 84, whiteSpace: "nowrap" }} align="center">
                            학생수
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5" }}>
                            근무 스케줄
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 84 }} align="center">
                            <TableSortLabel
                              active={orderBy === 'username'}
                              direction={orderBy === 'username' ? order : 'asc'}
                              onClick={() => handleRequestSort('username')}
                            >
                              이름
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 74 }} align="center">
                            <TableSortLabel
                              active={orderBy === 'member_type'}
                              direction={orderBy === 'member_type' ? order : 'asc'}
                              onClick={() => handleRequestSort('member_type')}
                            >
                              유형
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 86 }} align="center">
                            <TableSortLabel
                              active={orderBy === 'teacher'}
                              direction={orderBy === 'teacher' ? order : 'asc'}
                              onClick={() => handleRequestSort('teacher')}
                            >
                              선생님
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 66 }} align="center">
                            <TableSortLabel
                              active={orderBy === 'ticket'}
                              direction={orderBy === 'ticket' ? order : 'asc'}
                              onClick={() => handleRequestSort('ticket')}
                            >
                              티켓
                            </TableSortLabel>
                          </TableCell>
                          {!panelOpen && (
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 58 }} align="center">
                            알림
                          </TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 64 }} align="center">
                            연습실
                          </TableCell>
                          {!panelOpen && (
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 96 }} align="center">
                            <TableSortLabel
                              active={orderBy === 'lesson_start_date'}
                              direction={orderBy === 'lesson_start_date' ? order : 'asc'}
                              onClick={() => handleRequestSort('lesson_start_date')}
                            >
                              레슨시작일
                            </TableSortLabel>
                          </TableCell>
                          )}
                          {!panelOpen && (
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 300 }}>
                            레슨 일정
                          </TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? "grey.800" : "#f5f5f5", width: 100 }} align="center">
                            상태
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(tab === 0 ? teachers : filteredMembers)
                      .sort(getComparator(order, orderBy))
                      .map(item => (
                        <TableRow
                          key={item.id}
                          hover
                          selected={selected && selected.id === item.id}
                          onClick={() => {
                            if (panelOpen) {
                              openEdit(item, tab === 0);
                            } else {
                              setSelected({ id: item.id, isTeacher: tab === 0, member_type: item.member_type, originalUsername: item.username, name: item.name || item.username, practice_blocked_until: item.practice_blocked_until });
                            }
                          }}
                          onDoubleClick={() => openEdit(item, tab === 0)}
                          sx={{
                            cursor: "pointer",
                            opacity: item.is_active === false ? 0.6 : 1,
                            "&.Mui-selected": { 
                              backgroundColor: darkMode ? "primary.dark" : "primary.main",
                              "& td": { color: "primary.contrastText", fontWeight: 500 }
                            },
                          }}
                        >
                          {tab === 0 ? (
                            <>
                              <TableCell align="center">{item.username}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={item.is_active ? "활성" : "비활성"}
                                  size="small"
                                  color={item.is_active ? "success" : "default"}
                                />
                              </TableCell>
                              <TableCell align="center">{item.student_count || 0}</TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {item.schedules && item.schedules.length > 0 ? (
                                    item.schedules.map((s, idx) => (
                                      <Chip
                                        key={idx}
                                        label={`${({ MON: "월", TUE: "화", WED: "수", THU: "목", FRI: "금", SAT: "토" }[s.day])} ${s.start.slice(0, 5)}-${s.end.slice(0, 5)}`}
                                        size="small"
                                        sx={{
                                          bgcolor: darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd",
                                          color: "primary.main",
                                          fontWeight: 500,
                                          fontSize: "0.75rem"
                                        }}
                                      />
                                    ))
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">-</Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell align="center">{item.username}</TableCell>
                              <TableCell align="center">
                                {item.member_type === "COUNT" ? "횟수제" : item.member_type === "FIXED" ? "고정제" : "스케줄제"}
                              </TableCell>
                              <TableCell align="center">{item.teacher?.name || "-"}</TableCell>
                              <TableCell align="center">
                                <Box>
                                  <Typography variant="body2" component="span">{item.ticket ?? 0}</Typography>
                                  {(item.reschedule_ticket_count ?? 0) > 0 && (
                                    <Chip
                                      label={`변경권 ${item.reschedule_ticket_count}`}
                                      size="small"
                                      sx={{
                                        ml: 0.5,
                                        height: 18,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        backgroundColor: selected?.id === item.id
                                          ? 'rgba(255,255,255,0.25)'
                                          : (darkMode ? 'rgba(123,31,162,0.3)' : '#f3e5f5'),
                                        color: selected?.id === item.id
                                          ? '#fff'
                                          : (darkMode ? '#ce93d8' : '#7b1fa2'),
                                      }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              {!panelOpen && (
                              <TableCell align="center">
                                {item.has_push_subscription ? (
                                  <NotificationsActive 
                                    fontSize="small" 
                                    sx={{ color: selected?.id === item.id ? "inherit" : "success.main" }} 
                                  />
                                ) : (
                                  <NotificationsOff 
                                    fontSize="small" 
                                    sx={{ color: selected?.id === item.id ? "inherit" : "text.secondary" }} 
                                  />
                                )}
                              </TableCell>
                              )}
                              <TableCell align="center">
                                {item.practice_room_access ? "사용" : "-"}
                              </TableCell>
                              {!panelOpen && (<>
                              <TableCell align="center">
                                {item.member_type === "FIXED" && item.lesson_start_date ? (
                                  <Chip 
                                    label={item.lesson_start_date} 
                                    size="small"
                                    sx={{
                                      bgcolor: darkMode ? "rgba(76, 175, 80, 0.15)" : "#e8f5e9",
                                      color: "success.dark",
                                      fontWeight: 500,
                                      fontSize: "0.7rem"
                                    }}
                                  />
                                ) : "-"}
                              </TableCell>
                              <TableCell>
                                {item.member_type === "FIXED" && item.lesson_schedules && item.lesson_schedules.length > 0 ? (
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {item.lesson_schedules.map((s, idx) => (
                                      <Chip
                                        key={idx}
                                        label={`${({ MON: "월", TUE: "화", WED: "수", THU: "목", FRI: "금", SAT: "토" }[s.lesson_day])} ${s.lesson_start.slice(0, 5)}`}
                                        size="small"
                                        sx={{
                                          bgcolor: darkMode ? "rgba(255, 152, 0, 0.15)" : "#fff3e0",
                                          color: "warning.dark",
                                          fontWeight: 500,
                                          fontSize: "0.7rem"
                                        }}
                                      />
                                    ))}
                                  </Box>
                                ) : "-"}
                              </TableCell>
                              </>)}
                              <TableCell align="center">
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "center" }}>
                                  <Chip
                                    label={item.is_active ? "활성" : "비활성"}
                                    size="small"
                                    color={item.is_active ? "success" : "default"}
                                  />
                                  {item.practice_blocked_until && new Date(item.practice_blocked_until) > new Date() && (
                                    <Tooltip title={`연습실 예약 제한 (~${new Date(item.practice_blocked_until).toLocaleString('ko-KR')})`}>
                                      <Chip
                                        icon={<Block sx={{ fontSize: "0.9rem !important" }} />}
                                        label="예약제한"
                                        size="small"
                                        color="error"
                                        sx={{ fontSize: "0.7rem" }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
              </>
            )}
            {/* 모바일 정렬 메뉴 — 선택된 항목을 다시 누르면 오름/내림이 뒤집힌다 */}
            <Menu
              anchorEl={sortMenuAnchor}
              open={Boolean(sortMenuAnchor)}
              onClose={() => setSortMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {(tab === 0 ? TEACHER_SORT_OPTIONS : MEMBER_SORT_OPTIONS).map(o => (
                <MenuItem
                  key={o.key}
                  selected={orderBy === o.key}
                  onClick={() => { handleRequestSort(o.key); setSortMenuAnchor(null); }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {orderBy === o.key && <Check fontSize="small" color="primary" />}
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ sx: { fontWeight: orderBy === o.key ? 600 : 400 } }}>
                    {o.label}
                  </ListItemText>
                  {orderBy === o.key && (
                    order === 'asc'
                      ? <ArrowUpward sx={{ fontSize: "1rem", ml: 1, color: "text.secondary" }} />
                      : <ArrowDownward sx={{ fontSize: "1rem", ml: 1, color: "text.secondary" }} />
                  )}
                </MenuItem>
              ))}
            </Menu>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={closeRowMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {menuItem && getRowActions(menuItem, tab === 0).map(a => (
                <MenuItem
                  key={a.key}
                  disabled={a.disabled || loading}
                  onClick={() => { closeRowMenu(); a.onClick(); }}
                >
                  <ListItemIcon sx={{ color: `${a.color}.main`, minWidth: 36 }}>{a.icon}</ListItemIcon>
                  <ListItemText primaryTypographyProps={{ sx: { color: `${a.color}.main` } }}>{a.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
          </>
          )}
        </Box>
      </Box>

      {/* 수정/등록 다이얼로그 - Part 1 */}
      {/* keep-alive로 페이지가 숨겨져도 닫지 않고(open만 내려서) 편집 상태를 유지한 채 복귀 시 다시 띄운다 */}
      <Dialog
        open={openDialog && active}
        keepMounted
        onClose={() => { setOpenDialog(false); setLessonSchedules([]); setTeacherSchedules([]); setSimulationResult(null); }}
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
        hideBackdrop={!isMobile}
        disableEnforceFocus={!isMobile}
        disableScrollLock={!isMobile}
        transitionDuration={isMobile ? undefined : 0}
        sx={isMobile ? undefined : {
          // PC: 모달이 아닌 우측 도킹 사이드 패널 (목록을 가리지 않고 클릭도 통과)
          pointerEvents: "none",
          "& .MuiDialog-container": { alignItems: "flex-start", justifyContent: "flex-end" },
          "& .MuiDialog-paper": {
            pointerEvents: "auto",
            // 본문은 상단바(56)·하단바(56) 사이를 여백 없이 채우므로 패널도 같은 높이로 맞춘다.
            // 경계는 왼쪽 테두리 하나로만 주고, 위·아래는 각 바의 테두리가 대신한다.
            m: 0,
            mt: "56px",
            width: 620,
            maxWidth: "94vw",
            height: "calc(100vh - 112px)",
            maxHeight: "calc(100vh - 112px)",
            borderRadius: 0,
            borderLeft: 1,
            borderColor: "divider",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          {(!isMobile && selected?.id) ? (() => {
            const fullItem = (tab === 0 ? teachers : members).find(x => x.id === selected.id);
            const isT = tab === 0;
            const actions = fullItem ? getRowActions(fullItem, isT).filter(a => a.key !== "edit") : [];
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                <Avatar sx={{ width: 44, height: 44, fontSize: 15, fontWeight: 600, bgcolor: "primary.main", color: "primary.contrastText" }}>
                  {(fullItem?.name || fullItem?.username || form.name || "").slice(0, 2)}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: "1.05rem", fontWeight: 600 }} noWrap>{fullItem?.name || form.name || fullItem?.username}</Typography>
                    {!isT && fullItem && (
                      <Chip size="small" label={fullItem.member_type === "COUNT" ? "횟수제" : fullItem.member_type === "FIXED" ? "고정제" : "스케줄제"} sx={{ height: 20, fontSize: "0.7rem" }} />
                    )}
                    <Chip size="small" label={fullItem?.is_active === false ? "비활성" : "활성"} color={fullItem?.is_active === false ? "default" : "success"} sx={{ height: 20, fontSize: "0.7rem" }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                    {fullItem?.username}{!isT && fullItem?.teacher?.name ? ` · 담당 ${fullItem.teacher.name}` : ""}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end", rowGap: 0.25 }}>
                  {actions.map(a => (
                    <Tooltip key={a.key} title={a.label}>
                      <span>
                        <IconButton size="small" color={a.color} onClick={a.onClick} disabled={a.disabled || loading}>{a.icon}</IconButton>
                      </span>
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            );
          })() : (
            <Box sx={{ flex: 1 }}>{selected ? "정보 수정" : (tab === 0 ? "새 선생님 등록" : "새 회원 등록")}</Box>
          )}
          <IconButton edge="end" onClick={() => { setOpenDialog(false); setLessonSchedules([]); setTeacherSchedules([]); setSimulationResult(null); }} aria-label="닫기">
            <Close />
          </IconButton>
        </DialogTitle>

        {tab === 0 && selected?.id && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)}>
              <Tab label="기본 정보" />
              <Tab label="변동 스케줄" icon={<CalendarToday />} iconPosition="start" />
            </Tabs>
          </Box>
        )}

        <DialogContent sx={{ mt: 1, px: { xs: 2, sm: 3 } }}>
          {(tab === 1 || !selected?.id || dialogTab === 0) && (
            <Stack spacing={2} sx={{ pt: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                  {error.split('\n').map((line, idx) => (
                    <Box key={idx} sx={{ mb: idx < error.split('\n').length - 1 ? 0.5 : 0 }}>
                      {line}
                    </Box>
                  ))}
                </Alert>
              )}
              
              <TextField label="이름" name="memberName" autoComplete="off" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth required InputLabelProps={{ shrink: true }} />
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField 
                  label="아이디" 
                  name="memberUsername"
                  autoComplete="off"
                  value={form.username || ""} 
                  onChange={e => {
                    setForm({ ...form, username: e.target.value });
                    if (selected && e.target.value !== selected.originalUsername) {
                      setUsernameChanged(true);
                    } else {
                      setUsernameChanged(false);
                    }
                  }} 
                  fullWidth 
                  disabled={selected?.id && !usernameChanged}
                  required={!selected?.id}
                  InputLabelProps={{ shrink: true }}
                />
                {selected?.id && !usernameChanged && (
                  <Button 
                    variant="outlined" 
                    onClick={() => setUsernameChanged(true)}
                    sx={{ mt: 1, whiteSpace: 'nowrap' }}
                  >
                    수정하기
                  </Button>
                )}
                {selected?.id && usernameChanged && (
                  <Button 
                    variant="contained" 
                    color="warning"
                    onClick={() => {
                      setUsernameChanged(false);
                      setForm({ ...form, username: selected.originalUsername });
                    }}
                    sx={{ mt: 1, whiteSpace: 'nowrap' }}
                  >
                    취소
                  </Button>
                )}
              </Box>

              {selected?.id && !usernameChanged && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, ml: "14px", whiteSpace: "nowrap" }}>
                  우측 수정하기 버튼을 눌러 수정 모드 활성화
                </Typography>
              )}

              {usernameChanged && (
                <Alert severity="info">
                  아이디가 변경됩니다. 변경 후 사용자는 새 아이디로 로그인해야 합니다.
                </Alert>
              )}

              {!selected?.id && (
                <TextField
                  label="비밀번호"
                  type="text"
                  name="tempPassword"
                  autoComplete="off"
                  value={form.password || ""}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  fullWidth
                  required
                  slotProps={{
                    htmlInput: {
                      autoCapitalize: "none",
                      autoCorrect: "off",
                      spellCheck: false,
                      style: showTempPw ? undefined : { WebkitTextSecurity: "disc" },
                    },
                    inputLabel: { shrink: true },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowTempPw(prev => !prev)} edge="end" aria-label="비밀번호 표시 전환">
                            {showTempPw ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}

              {/* 선생님 스케줄 */}
              {tab === 0 && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>근무 스케줄</Typography>
                    <Button size="small" startIcon={<Add />} onClick={addTeacherSchedule}>추가</Button>
                  </Box>
                  {teacherSchedules.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">근무 스케줄을 추가해주세요</Typography>
                  ) : formNarrow ? (
                    <Stack spacing={1}>
                      {teacherSchedules.map((s, i) => (
                        <Box key={i} sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 1.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">스케줄 {i + 1}</Typography>
                            <IconButton size="small" color="error" onClick={() => deleteTeacherSchedule(i)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                            <InputLabel>요일</InputLabel>
                            <Select label="요일" value={s.day} onChange={e => updateTeacherSchedule(i, "day", e.target.value)}>
                              {DAY_OPTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                            </Select>
                          </FormControl>
                          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>시작</InputLabel>
                              <Select label="시작" value={s.start} onChange={e => updateTeacherSchedule(i, "start", e.target.value)}>
                                {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                              </Select>
                            </FormControl>
                            <FormControl size="small" fullWidth>
                              <InputLabel>종료</InputLabel>
                              <Select label="종료" value={s.end} onChange={e => updateTeacherSchedule(i, "end", e.target.value)}>
                                {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>요일</TableCell>
                            <TableCell>시작</TableCell>
                            <TableCell>종료</TableCell>
                            <TableCell align="center">삭제</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {teacherSchedules.map((s, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Select size="small" value={s.day} onChange={e => updateTeacherSchedule(i, "day", e.target.value)}>
                                  {DAY_OPTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Select size="small" value={s.start} onChange={e => updateTeacherSchedule(i, "start", e.target.value)}>
                                  {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Select size="small" value={s.end} onChange={e => updateTeacherSchedule(i, "end", e.target.value)}>
                                  {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </Select>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton size="small" color="error" onClick={() => deleteTeacherSchedule(i)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* 회원 정보 */}
              {tab === 1 && (
                <>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel shrink>회원 유형</InputLabel>
                      <Select value={form.member_type || ""} onChange={e => setForm({ ...form, member_type: e.target.value })} notched label="회원 유형">
                        <MenuItem value="COUNT">횟수제</MenuItem>
                        <MenuItem value="FIXED">고정제</MenuItem>
                        <MenuItem value="SCHEDULE">스케줄제</MenuItem>
                      </Select>
                    </FormControl>

                    {form.member_type === "SCHEDULE" ? (
                      <FormControl fullWidth>
                        <InputLabel shrink>주당 레슨 횟수</InputLabel>
                        <Select
                          value={form.lessons_per_week || ""}
                          onChange={e => setForm({ ...form, lessons_per_week: Number(e.target.value) })}
                          notched
                          label="주당 레슨 횟수"
                        >
                          <MenuItem value={1}>주 1회 (결제시 4티켓)</MenuItem>
                          <MenuItem value={2}>주 2회 (결제시 8티켓)</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <FormControl fullWidth>
                        <InputLabel shrink>담당 선생님</InputLabel>
                        <Select value={form.teacher_id || ""} onChange={e => setForm({ ...form, teacher_id: e.target.value })} notched label="담당 선생님">
                          {teacherOptions.filter(t => t.is_active).map(t => (
                            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>

                  {form.member_type === "SCHEDULE" && (
                    <FormControl fullWidth>
                      <InputLabel shrink>담당 선생님</InputLabel>
                      <Select value={form.teacher_id || ""} onChange={e => setForm({ ...form, teacher_id: e.target.value })} notched label="담당 선생님">
                        {teacherOptions.filter(t => t.is_active).map(t => (
                          <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {form.member_type === "SCHEDULE" && (
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>티켓 유효기간 현황</Typography>
                      {ticketBatches.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                          티켓 유효기간 내역이 없습니다.
                        </Typography>
                      ) : formNarrow ? (
                        <Stack spacing={1}>
                          {ticketBatches.map((batch) => {
                            const today = new Date().toISOString().slice(0, 10);
                            const isExpired = batch.expires_at < today;
                            const sourceMap = { ADMIN_GRANT: '관리자 부여', PAYMENT: '결제', CANCEL_REFUND: '레슨 취소', HOLIDAY_REFUND: '휴일 환불', WORKDAY_REFUND: '업무일' };
                            return (
                              <Box key={batch.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, opacity: (isExpired || batch.remaining === 0) ? 0.6 : 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                      {new Date(batch.createdAt || batch.created_at).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })}
                                    </Typography>
                                    <Chip label={sourceMap[batch.source] || batch.source} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                                  </Box>
                                  <Box sx={{ flexShrink: 0 }}>
                                    <IconButton size="small" onClick={() => { setEditingBatchId(batch.id); setEditingExpiresAt(batch.expires_at); }}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDeleteBatch(selected.id, batch.id, batch.remaining)} disabled={loading}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                  <Typography variant="body2" color="text.secondary">잔여 / 총량</Typography>
                                  <Typography variant="body2">
                                    <Box component="span" sx={{ fontWeight: 600, color: batch.remaining > 0 ? 'success.main' : 'text.disabled' }}>{batch.remaining}</Box> / {batch.total_amount}
                                  </Typography>
                                </Box>
                                {editingBatchId === batch.id ? (
                                  <Box sx={{ mt: 1 }}>
                                    <DateField
                                      label="유효기간"
                                      size="small"
                                      fullWidth
                                      value={editingExpiresAt}
                                      onChange={setEditingExpiresAt}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                      <Button size="small" variant="contained" fullWidth onClick={() => handleUpdateBatchExpiry(selected.id, batch.id)} disabled={loading}>저장</Button>
                                      <Button size="small" fullWidth onClick={() => setEditingBatchId(null)}>취소</Button>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">유효기간</Typography>
                                    <Typography variant="body2" color={isExpired ? 'error' : 'text.primary'}>
                                      {batch.expires_at}{isExpired ? ' · 만료' : ''}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <TableContainer sx={{ maxHeight: 200 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.6 }}>발급일</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.6 }}>출처</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.6 }} align="center">총량</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.6 }} align="center">잔여</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.6 }}>유효기간</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.6 }} align="center">수정</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.6 }} align="center">삭제</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {ticketBatches.map((batch) => {
                                const today = new Date().toISOString().slice(0, 10);
                                const isExpired = batch.expires_at < today;
                                const sourceMap = { ADMIN_GRANT: '관리자 부여', PAYMENT: '결제', CANCEL_REFUND: '레슨 취소', HOLIDAY_REFUND: '휴일 환불', WORKDAY_REFUND: '업무일' };
                                return (
                                  <TableRow key={batch.id} sx={{ opacity: (isExpired || batch.remaining === 0) ? 0.5 : 1 }}>
                                    <TableCell sx={{ fontSize: '0.8rem', py: 0.4 }}>
                                      {new Date(batch.createdAt || batch.created_at).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem', py: 0.4 }}>
                                      <Chip label={sourceMap[batch.source] || batch.source} size="small" variant="outlined" sx={{ fontSize: '0.72rem', height: 20 }} />
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: '0.8rem', py: 0.4 }}>{batch.total_amount}</TableCell>
                                    <TableCell align="center" sx={{ py: 0.3 }}>
                                      <Chip label={batch.remaining} size="small" color={batch.remaining > 0 ? 'success' : 'default'} variant={batch.remaining > 0 ? 'filled' : 'outlined'} sx={{ fontSize: '0.72rem', height: 20 }} />
                                    </TableCell>
                                    <TableCell sx={{ py: 0.3 }}>
                                      {editingBatchId === batch.id ? (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <DateField
                                            size="small"
                                            value={editingExpiresAt}
                                            onChange={setEditingExpiresAt}
                                            sx={{ width: 150 }}
                                          />
                                          <Button size="small" variant="contained" onClick={() => handleUpdateBatchExpiry(selected.id, batch.id)} disabled={loading} sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}>저장</Button>
                                          <Button size="small" onClick={() => setEditingBatchId(null)} sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}>취소</Button>
                                        </Stack>
                                      ) : (
                                        <Chip label={batch.expires_at} size="small" color={isExpired ? 'error' : 'default'} variant={isExpired ? 'outlined' : 'filled'} sx={{ fontSize: '0.72rem', height: 20 }} />
                                      )}
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 0.3 }}>
                                      <Tooltip title="유효기간 수정">
                                        <IconButton size="small" onClick={() => { setEditingBatchId(batch.id); setEditingExpiresAt(batch.expires_at); }}>
                                          <Edit fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 0.3 }}>
                                      <Tooltip title="배치 삭제">
                                        <IconButton size="small" color="error" onClick={() => handleDeleteBatch(selected.id, batch.id, batch.remaining)} disabled={loading}>
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>티켓 부여</Typography>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1}>
                          <TextField
                            label="티켓 수"
                            type="number"
                            size="small"
                            value={grantForm.amount}
                            onChange={e => setGrantForm({ ...grantForm, amount: Number(e.target.value) })}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: 1 }}
                            sx={{ width: 100 }}
                          />
                          <DateField
                            label="유효기간"
                            size="small"
                            value={grantForm.expires_at}
                            onChange={expires_at => setGrantForm({ ...grantForm, expires_at })}
                            fullWidth
                          />
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            label="메모 (선택)"
                            size="small"
                            value={grantForm.description}
                            onChange={e => setGrantForm({ ...grantForm, description: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            placeholder="예: 3월 결제분"
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleGrantTickets}
                            disabled={loading || !grantForm.expires_at || !grantForm.amount}
                            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                          >
                            부여
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  )}

                  {form.member_type === "SCHEDULE" && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    <div>아래는 <strong>총 티켓 수</strong>입니다. 일반 티켓을 <strong>늘리려면</strong> 위의 <strong>‘티켓 부여’</strong>를 사용하세요.</div>
                    <div>가급적 아래는 건들지마세요!!</div>
                  </Alert>
                  )}
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, alignItems: "start" }}>
                    {(form.member_type === "COUNT" || form.member_type === "FIXED") && (
                      <TextField
                        label="총 티켓 수 (일반+변경권)"
                        type="number"
                        value={form.ticket || 0}
                        onChange={e => setForm({ ...form, ticket: Number(e.target.value) })}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        helperText={`일반 ${Math.max(0, (form.ticket ?? 0) - (form.reschedule_ticket_count ?? 0))}장 + 변경권 ${form.reschedule_ticket_count ?? 0}장`}
                        inputProps={{ min: 0 }}
                      />
                    )}
                    {form.member_type === "SCHEDULE" && (
                      <TextField
                        label="총 티켓 수 (일반+변경권)"
                        type="number"
                        value={form.ticket ?? 0}
                        onChange={e => setForm({ ...form, ticket: Number(e.target.value) })}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        helperText={`일반 ${Math.max(0, (form.ticket ?? 0) - (form.reschedule_ticket_count ?? 0))}장 + 변경권 ${form.reschedule_ticket_count ?? 0}장 · 수정 X`}
                        inputProps={{ min: 0 }}
                      />
                    )}
                    <TextField
                      label={form.member_type === "SCHEDULE" ? "남은 변경권 티켓 수" : "변경권 티켓 수"}
                      type="number"
                      value={form.reschedule_ticket_count ?? 0}
                      onChange={e => setForm({ ...form, reschedule_ticket_count: Number(e.target.value) })}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      helperText={<>이전 레슨 취소로 발급된 티켓 수<br />(이 티켓으로 예약한 레슨은 재취소 불가)</>}
                      inputProps={{ min: 0, max: form.ticket || 0 }}
                    />
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, alignItems: "stretch" }}>
                    <FormControl fullWidth>
                      <InputLabel shrink>평일 연습실 시간</InputLabel>
                      <Select
                        value={form.practice_before_lesson ? "before" : "after"}
                        onChange={e => setForm({ ...form, practice_before_lesson: e.target.value === "before" })}
                        notched
                        label="평일 연습실 시간"
                      >
                        <MenuItem value="before">레슨 전 30분</MenuItem>
                        <MenuItem value="after">레슨 후 30분</MenuItem>
                      </Select>
                    </FormControl>

                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      height: 56,
                      px: 1.5,
                      bgcolor: darkMode ? "grey.800" : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider"
                    }}>
                      <FormControlLabel
                        sx={{ m: 0, "& .MuiFormControlLabel-label": { fontWeight: 500, fontSize: 16 } }}
                        control={
                          <Checkbox
                            checked={form.practice_room_access || false}
                            onChange={(e) => setForm({ ...form, practice_room_access: e.target.checked })}
                          />
                        }
                        label="연습실 이용"
                      />
                    </Box>
                  </Box>

                  {/* 고정제 레슨 스케줄 */}
                  {form.member_type === "FIXED" && (
                    <Box>
                      <DateField
                        label="레슨 시작일"
                        value={form.lesson_start_date || ""}
                        onChange={lesson_start_date => setForm({ ...form, lesson_start_date })}
                        fullWidth
                        required
                        sx={{ mb: 1 }}
                        helperText="레슨 시작일부터 1개월간 자동 예약이 생성됩니다"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.skip_practice !== true}
                            onChange={e => setForm({ ...form, skip_practice: !e.target.checked })}
                          />
                        }
                        label="연습실 자동 생성"
                        sx={{ mb: 1 }}
                      />

                      {selected && (
                        <DateField
                          label="스케줄 변경 적용 시작일"
                          value={scheduleApplyFrom}
                          onChange={setScheduleApplyFrom}
                          size="small"
                          fullWidth
                          sx={{ mb: 2 }}
                          helperText="이 날짜부터 예약이 새 스케줄로 재생성됩니다. 이전 예약은 유지됩니다."
                        />
                      )}

                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>고정 레슨 스케줄</Typography>
                        <Button size="small" startIcon={<Add />} onClick={addLessonSchedule}>추가</Button>
                      </Box>
                      {lessonSchedules.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">스케줄을 추가해주세요</Typography>
                      ) : formNarrow ? (
                        <Stack spacing={1}>
                          {lessonSchedules.map((s, i) => (
                            <Box key={i} sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 1.5 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">레슨 {i + 1}</Typography>
                                <IconButton size="small" color="error" onClick={() => deleteLessonSchedule(i)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                              <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                                <InputLabel>요일</InputLabel>
                                <Select label="요일" value={s.day} onChange={e => updateLessonSchedule(i, "day", e.target.value)}>
                                  {DAY_OPTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                                </Select>
                              </FormControl>
                              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>시작</InputLabel>
                                  <Select label="시작" value={s.start} onChange={e => updateLessonSchedule(i, "start", e.target.value)}>
                                    {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                  </Select>
                                </FormControl>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>종료</InputLabel>
                                  <Select label="종료" value={s.end} onChange={e => updateLessonSchedule(i, "end", e.target.value)}>
                                    {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                  </Select>
                                </FormControl>
                              </Box>
                              <FormControl size="small" fullWidth>
                                <InputLabel>선생님</InputLabel>
                                <Select label="선생님" value={s.teacher_id} onChange={e => updateLessonSchedule(i, "teacher_id", e.target.value)}>
                                  {teacherOptions.filter(t => t.is_active).map(t => (
                                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>요일</TableCell>
                                <TableCell>시작</TableCell>
                                <TableCell>종료</TableCell>
                                <TableCell>선생님</TableCell>
                                <TableCell align="center">삭제</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lessonSchedules.map((s, i) => (
                                <TableRow key={i}>
                                  <TableCell>
                                    <Select size="small" value={s.day} onChange={e => updateLessonSchedule(i, "day", e.target.value)}>
                                      {DAY_OPTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                                    </Select>
                                  </TableCell>
                                  <TableCell>
                                    <Select size="small" value={s.start} onChange={e => updateLessonSchedule(i, "start", e.target.value)}>
                                      {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                    </Select>
                                  </TableCell>
                                  <TableCell>
                                    <Select size="small" value={s.end} onChange={e => updateLessonSchedule(i, "end", e.target.value)}>
                                      {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                    </Select>
                                  </TableCell>
                                  <TableCell>
                                    <Select size="small" value={s.teacher_id} onChange={e => updateLessonSchedule(i, "teacher_id", e.target.value)}>
                                      {teacherOptions.filter(t => t.is_active).map(t => (
                                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                      ))}
                                    </Select>
                                  </TableCell>
                                  <TableCell align="center">
                                    <IconButton size="small" color="error" onClick={() => deleteLessonSchedule(i)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      {/* 충돌 미리보기 */}
                      {lessonSchedules.length > 0 && (
                        <ScheduleConflictPreview
                          memberId={selected?.id}
                          newSchedules={lessonSchedules}
                          onSimulationComplete={setSimulationResult}
                        />
                      )}

                      {/* 다른 고정제 회원과 자리가 겹치면 플래너로 유도 (스왑/체인 1회 적용) */}
                      {simulationResult && !simulationResult.canProceed && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SwapHoriz />}
                          sx={{ mt: 1 }}
                          onClick={() => {
                            setOpenDialog(false);
                            setSelected(null);
                            setSection("planner");
                            setTab(3);
                          }}
                        >
                          스케줄 플래너에서 해결 (맞바꾸기 · 체인)
                        </Button>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Stack>
          )}

          {/* 선생님 변동 스케줄 탭 */}
          {tab === 0 && selected?.id && dialogTab === 1 && (
            <Stack spacing={2}>
              <Alert severity="info">
                선생님의 휴가 또는 근무 시간 변경시 변동되는 스케줄을 추가합니다.
              </Alert>

              <Box sx={{ p: 2, bgcolor: darkMode ? "grey.800" : "#f5f5f5", borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={2}>새 변동 스케줄 등록</Typography>
                
                <Stack spacing={2}>
                  <DateField
                    label="날짜"
                    value={overrideForm.date}
                    onChange={date => setOverrideForm({ ...overrideForm, date })}
                    fullWidth
                    disablePast
                  />

                  <FormControl fullWidth>
                    <InputLabel>변동 유형</InputLabel>
                    <Select
                      label="변동 유형"
                      value={overrideForm.type}
                      onChange={e => setOverrideForm({ ...overrideForm, type: e.target.value })}
                    >
                      <MenuItem value="OFF">휴가</MenuItem>
                      <MenuItem value="ADJUSTED">근무 시간 조정</MenuItem>
                    </Select>
                  </FormControl>

                  {overrideForm.type === "ADJUSTED" && (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>시작 시간</InputLabel>
                        <Select
                          label="시작 시간"
                          value={overrideForm.work_start}
                          onChange={e => setOverrideForm({ ...overrideForm, work_start: e.target.value })}
                        >
                          {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth>
                        <InputLabel>종료 시간</InputLabel>
                        <Select
                          label="종료 시간"
                          value={overrideForm.work_end}
                          onChange={e => setOverrideForm({ ...overrideForm, work_end: e.target.value })}
                        >
                          {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </>
                  )}

                  <TextField
                    label="변동 사유 (선택)"
                    value={overrideForm.reason}
                    onChange={e => setOverrideForm({ ...overrideForm, reason: e.target.value })}
                    fullWidth
                    multiline
                    rows={2}
                  />

                  <Button 
                    variant="contained" 
                    onClick={handleAddOverride}
                    disabled={loading}
                    fullWidth
                  >
                    등록
                  </Button>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>등록된 변동 스케줄</Typography>
                
                {scheduleOverrides.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">등록된 변동 스케줄이 없습니다.</Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>날짜</TableCell>
                          <TableCell>유형</TableCell>
                          <TableCell>시간</TableCell>
                          <TableCell>사유</TableCell>
                          <TableCell align="center">삭제</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {upcomingOverrides.map(override => (
                          <TableRow key={override.id}>
                            <TableCell>{override.date}</TableCell>
                            <TableCell>
                              <Chip
                                label={override.type === "OFF" ? "휴가" : "시간 조정"}
                                size="small"
                                color={override.type === "OFF" ? "error" : "warning"}
                              />
                            </TableCell>
                            <TableCell>
                              {override.type === "OFF"
                                ? "출근 안 함"
                                : `${override.work_start?.slice(0, 5)} - ${override.work_end?.slice(0, 5)}`
                              }
                            </TableCell>
                            <TableCell>{override.reason || "-"}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteOverride(override.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        {pastOverrides.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ py: 0.5, bgcolor: "action.hover" }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                지난 변동 (열람 전용)
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {pastOverrides.map(override => (
                          <TableRow key={override.id} sx={{ opacity: 0.55 }}>
                            <TableCell>{override.date}</TableCell>
                            <TableCell>
                              <Chip
                                label={override.type === "OFF" ? "휴가" : "시간 조정"}
                                size="small"
                                color={override.type === "OFF" ? "error" : "warning"}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {override.type === "OFF"
                                ? "출근 안 함"
                                : `${override.work_start?.slice(0, 5)} - ${override.work_end?.slice(0, 5)}`
                              }
                            </TableCell>
                            <TableCell>{override.reason || "-"}</TableCell>
                            <TableCell align="center">-</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 1 }, "& > :not(style)": { flex: { xs: 1, sm: "initial" } } }}>
          <Button onClick={() => { setOpenDialog(false); setSimulationResult(null); }}>취소</Button>
          {(tab === 1 || !selected?.id || dialogTab === 0) && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              color={simulationResult && !simulationResult.canProceed ? "error" : "primary"}
            >
              {loading ? "처리 중..." : simulationResult && !simulationResult.canProceed ? "충돌 — 저장 불가" : "저장"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 비밀번호 초기화 다이얼로그 */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>비밀번호 초기화</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1}>
            새로운 비밀번호를 입력하세요.
          </Typography>
          <TextField
            label="새 비밀번호"
            type="text"
            name="resetPassword"
            autoComplete="off"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            fullWidth
            slotProps={{
              htmlInput: {
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
                style: showResetPw ? undefined : { WebkitTextSecurity: "disc" },
              },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowResetPw(prev => !prev)} edge="end" aria-label="비밀번호 표시 전환">
                      {showResetPw ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>취소</Button>
          <Button
            variant="contained"
            disabled={loading}
            onClick={async () => {
              if (!resetPassword.trim()) {
                showSnackbar("비밀번호를 입력하세요.", 'warning');
                return;
              }
              try {
                setLoading(true);
                await api.post(`/members/${selected.id}/reset-password`, { newPassword: resetPassword });
                showSnackbar("비밀번호가 초기화되었습니다.", "success");
                setResetDialogOpen(false);
              } catch (e) {
                const errorMessage = e?.response?.data?.message || "비밀번호 초기화에 실패했습니다.";
                showSnackbar(errorMessage, "error");
              } finally {
                setLoading(false);
              }
            }}
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 학생 이관 다이얼로그 */}
      <Dialog 
        open={transferDialogOpen && active} 
        fullScreen={isMobile}
        onClose={() => {
          setTransferDialogOpen(false);
          fetchAll();
        }} 
        fullWidth 
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          선생님 이관: {transferData.oldTeacherName}
          <Typography variant="body2" color="text.secondary">
            {transferData.step === 1 && "1단계: 학생 선택 및 새 선생님 지정"}
            {transferData.step === 2 && "2단계: 레슨 예약 이관"}
            {transferData.step === 3 && "3단계: 완료"}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          {transferData.step === 1 && (
            <Stack spacing={2}>
              <Alert severity="info">
                이관할 학생을 선택하고 각 학생의 새 담당 선생님을 지정해주세요.
              </Alert>
              
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={transferData.candidates.length > 0 && transferData.candidates.every(c => c.selected)}
                          indeterminate={transferData.candidates.some(c => c.selected) && !transferData.candidates.every(c => c.selected)}
                          onChange={(e) => {
                            setTransferData(prev => ({
                              ...prev,
                              candidates: prev.candidates.map(c => ({
                                ...c,
                                selected: e.target.checked
                              }))
                            }));
                          }}
                        />
                      </TableCell>
                      <TableCell>학생명</TableCell>
                      <TableCell>유형</TableCell>
                      <TableCell>잔여 레슨</TableCell>
                      <TableCell>새 선생님</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transferData.candidates.map(c => (
                      <TableRow key={c.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={c.selected}
                            onChange={(e) => updateTransferCandidate(c.id, 'selected', e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>{c.member_type === 'FIXED' ? '고정제' : '횟수제'}</TableCell>
                        <TableCell>
                          {c.future_reservations > 0 ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => viewStudentLessons(c)}
                              sx={{ minWidth: 'auto' }}
                            >
                              {c.future_reservations}건
                            </Button>
                          ) : (
                            <Typography variant="body2">0건</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={c.new_teacher_id}
                            onChange={(e) => updateTransferCandidate(c.id, 'new_teacher_id', e.target.value)}
                            disabled={!c.selected}
                            sx={{ minWidth: 120 }}
                          >
                            {teachers
                              .filter(t => t.is_active && t.id !== transferData.oldTeacherId)
                              .map(t => (
                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                              ))
                            }
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Typography variant="body2" color="text.secondary">
                선택된 학생: {transferData.candidates.filter(c => c.selected).length}명
              </Typography>
            </Stack>
          )}
          
          {transferData.step === 2 && (
            <Stack spacing={2}>
              <Alert severity="warning">
                선택한 학생들의 레슨 예약을 새 선생님에게 이관합니다.
                지정한 날짜 이후의 모든 레슨 예약이 변경됩니다.
              </Alert>
              
              <DateField
                label="이관 시작 날짜"
                value={transferData.fromDate}
                onChange={(fromDate) => setTransferData(prev => ({ ...prev, fromDate }))}
                fullWidth
                helperText="이 날짜 이후의 레슨 예약이 새 선생님에게 이관됩니다."
              />
              
              <Box sx={{ p: 2, bgcolor: darkMode ? "grey.800" : "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>이관 대상</Typography>
                {transferData.candidates.filter(c => c.selected).map(c => {
                  const newTeacher = teachers.find(t => t.id === c.new_teacher_id);
                  return (
                    <Typography key={c.id} variant="body2">
                      • {c.name} → {newTeacher?.name} ({c.future_reservations}건)
                    </Typography>
                  );
                })}
              </Box>
            </Stack>
          )}
          
          {transferData.step === 3 && (
            <Stack spacing={2}>
              <Alert severity="success">
                학생 및 레슨 이관이 완료되었습니다!
              </Alert>
              
              <Typography>
                이제 {transferData.oldTeacherName} 선생님을 비활성화할 수 있습니다.
                비활성화하면 새 예약/회원 등록 시 선택할 수 없게 됩니다.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => {
            setTransferDialogOpen(false);
            fetchAll();
          }}>
            {transferData.step === 3 ? '닫기' : '취소'}
          </Button>
          
          {transferData.step === 1 && (
            <Button 
              variant="contained" 
              onClick={handleTransferStudents}
              disabled={loading || transferData.candidates.filter(c => c.selected).length === 0}
            >
              {loading ? '처리 중...' : '학생 이관'}
            </Button>
          )}
          
          {transferData.step === 2 && (
            <>
              <Button onClick={() => setTransferData(prev => ({ ...prev, step: 1 }))}>
                이전
              </Button>
              <Button 
                variant="contained" 
                onClick={handleTransferReservations}
                disabled={loading}
              >
                {loading ? '처리 중...' : '레슨 이관'}
              </Button>
            </>
          )}
          
          {transferData.step === 3 && (
            <Button 
              variant="contained" 
              color="warning"
              onClick={handleDeactivateTeacher}
            >
              선생님 비활성화
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 레슨 상세 정보 다이얼로그 */}
      <Dialog
        open={lessonDetailDialog.open}
        onClose={() => setLessonDetailDialog({ open: false, studentName: "", oldTeacherName: "", lessons: [] })}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {lessonDetailDialog.studentName}님의 {lessonDetailDialog.oldTeacherName} 선생님 레슨
          <Typography variant="body2" color="text.secondary">
            이관 대상: 총 {lessonDetailDialog.lessons.length}건
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          {lessonDetailDialog.lessons.length === 0 ? (
            <Typography color="text.secondary">이관할 잔여 레슨이 없습니다.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>날짜</TableCell>
                    <TableCell>시간</TableCell>
                    <TableCell>상태</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lessonDetailDialog.lessons.map((lesson, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{lesson.date}</TableCell>
                      <TableCell>{lesson.start_time.slice(0, 5)}</TableCell>
                      <TableCell>
                        <Chip
                          label={lesson.is_provisional ? "임시 예약" : "정식 예약"}
                          size="small"
                          color={lesson.is_provisional ? "default" : "primary"}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setLessonDetailDialog({ open: false, studentName: "", oldTeacherName: "", lessons: [] })}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 티켓 이력 다이얼로그 — 거래내역식 타임라인 */}
      <Dialog
        open={ticketHistoryDialog.open}
        onClose={() => setTicketHistoryDialog({ open: false, memberName: "", ticket: 0, reschedule: 0 })}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { height: isMobile ? "100%" : "82vh", maxHeight: isMobile ? "100%" : 760 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pb: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={600} lineHeight={1.2} noWrap>
              {ticketHistoryDialog.memberName}
            </Typography>
            <Typography variant="caption" color="text.secondary">티켓 이력</Typography>
          </Box>
          <IconButton
            edge="end"
            onClick={() => setTicketHistoryDialog({ open: false, memberName: "", ticket: 0, reschedule: 0 })}
            aria-label="닫기"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {/* 요약 + 필터 (스크롤 시 상단 고정) */}
          <Box sx={{ position: "sticky", top: 0, zIndex: 2, bgcolor: "background.paper" }}>
            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
              <Box>
                <Typography variant="caption" color="text.secondary">현재 잔여</Typography>
                <Typography sx={{ fontSize: "1.6rem", fontWeight: 600, lineHeight: 1.1 }}>
                  {ticketHistoryDialog.ticket}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.25 }}>장</Typography>
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2.5, textAlign: "right" }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">일반</Typography>
                  <Typography sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
                    {Math.max(0, ticketHistoryDialog.ticket - ticketHistoryDialog.reschedule)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="primary" display="block">변경권</Typography>
                  <Typography sx={{ fontSize: "1.1rem", fontWeight: 600 }} color="primary">
                    {ticketHistoryDialog.reschedule}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1, px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
              {[
                { key: "all", label: "전체" },
                { key: "in", label: "적립 +" },
                { key: "out", label: "사용 −" },
              ].map((f) => (
                <Chip
                  key={f.key}
                  label={f.label}
                  size="small"
                  onClick={() => setTicketHistoryFilter(f.key)}
                  color={ticketHistoryFilter === f.key ? "primary" : "default"}
                  variant={ticketHistoryFilter === f.key ? "filled" : "outlined"}
                />
              ))}
            </Box>
          </Box>

          {/* 거래내역 리스트 */}
          {(() => {
            const filtered = ticketHistory.filter((r) =>
              ticketHistoryFilter === "in" ? r.change_amount > 0
                : ticketHistoryFilter === "out" ? r.change_amount < 0
                  : true
            );
            if (filtered.length === 0) {
              return (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
                  {ticketHistory.length === 0 ? "티켓 변동 이력이 없습니다." : "해당하는 내역이 없습니다."}
                </Typography>
              );
            }
            let lastLabel = null;
            const nodes = [];
            filtered.forEach((row) => {
              const label = ticketDateLabel(row.createdAt);
              if (label !== lastLabel) {
                lastLabel = label;
                nodes.push(
                  <Box key={`grp-${row.id}`} sx={{ px: 2, py: 0.75, bgcolor: "action.hover" }}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "text.secondary" }}>{label}</Typography>
                  </Box>
                );
              }
              const cfg = TICKET_REASON_CONFIG[row.reason] || { label: row.reason, Icon: History };
              const Icon = cfg.Icon;
              const kindColor = row.change_amount > 0 ? "success" : row.change_amount < 0 ? "error" : "info";
              const amountStr = `${row.change_amount > 0 ? "+" : ""}${row.change_amount}`;
              nodes.push(
                <Box key={row.id} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", "&:hover": { bgcolor: "action.hover" } }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: (t) => alpha(t.palette[kindColor].main, 0.14), color: `${kindColor}.main` }}>
                    <Icon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{cfg.label}</Typography>
                      {row.reason === "LESSON_CANCEL" && (
                        <Chip label="변경권" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: "0.62rem", "& .MuiChip-label": { px: 0.75 } }} />
                      )}
                    </Box>
                    {row.description && (
                      <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mt: 0.25, lineHeight: 1.4 }}>{stripSeconds(row.description)}</Typography>
                    )}
                    <Typography sx={{ fontSize: "0.72rem", color: "text.disabled", mt: 0.4 }}>
                      {ticketTimeLabel(row.createdAt)}{row.creator?.name ? ` · ${row.creator.name}` : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.92rem", color: row.change_amount === 0 ? "text.secondary" : `${kindColor}.main` }}>{amountStr}</Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "text.disabled", mt: 0.25 }}>잔 {row.new_ticket}</Typography>
                  </Box>
                </Box>
              );
            });
            return nodes;
          })()}
        </DialogContent>
      </Dialog>

      {/* 레슨 이력 다이얼로그 — 시작일은 기본 비움(무제한)이고, 스크롤로 과거 레슨을 이어서 불러온다 */}
      <Dialog
        open={lessonHistoryDialog.open}
        onClose={() => setLessonHistoryDialog((d) => ({ ...d, open: false }))}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { height: isMobile ? "100%" : "82vh", maxHeight: isMobile ? "100%" : 760 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pb: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={600} lineHeight={1.2} noWrap>
              {lessonHistoryDialog.memberName}
            </Typography>
            <Typography variant="caption" color="text.secondary">레슨 이력</Typography>
          </Box>
          <IconButton
            edge="end"
            onClick={() => setLessonHistoryDialog((d) => ({ ...d, open: false }))}
            aria-label="닫기"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }} onScroll={handleLessonHistoryScroll}>
          {(() => {
            const todayStr = dayjs().format("YYYY-MM-DD");
            const monthCount = {};
            lessonHistory.forEach((r) => {
              const m = dayjs(r.date).format("YYYY년 M월");
              monthCount[m] = (monthCount[m] || 0) + 1;
            });

            return (
              <>
                {/* 요약 + 기간/선생님 필터 (스크롤 시 상단 고정) */}
                <Box sx={{ position: "sticky", top: 0, zIndex: 2, bgcolor: "background.paper" }}>
                  <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">선택 기간 레슨</Typography>
                      <Typography sx={{ fontSize: "1.6rem", fontWeight: 600, lineHeight: 1.1 }}>
                        {lessonHistoryTotal}
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.25 }}>회</Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="caption" color="text.secondary" display="block">최근 레슨</Typography>
                      <Typography sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
                        {lessonHistory[0] ? dayjs(lessonHistory[0].date).format("MM.DD") : "-"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", px: 2, py: 1.75, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flex: { xs: "1 1 100%", sm: "1 1 auto" }, minWidth: 0 }}>
                      <DateField
                        label="시작일"
                        size="small"
                        value={lessonHistoryDialog.from}
                        onChange={(v) => changeLessonHistoryRange("from", v)}
                        maxDate={lessonHistoryDialog.to || todayStr}
                        sx={{ flex: 1, minWidth: 0 }}
                      />
                      {lessonHistoryDialog.from && (
                        <Tooltip title="시작일 제한 없음으로">
                          <IconButton size="small" onClick={() => changeLessonHistoryRange("from", "")} sx={{ flexShrink: 0 }}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>~</Typography>
                      <DateField
                        label="종료일"
                        size="small"
                        value={lessonHistoryDialog.to}
                        onChange={(v) => changeLessonHistoryRange("to", v)}
                        minDate={lessonHistoryDialog.from || undefined}
                        maxDate={todayStr}
                        sx={{ flex: 1, minWidth: 0 }}
                      />
                    </Box>
                    <FormControl size="small" sx={{ flex: { xs: "1 1 100%", sm: "0 0 132px" } }}>
                      <InputLabel id="lesson-history-teacher-label">선생님</InputLabel>
                      <Select
                        labelId="lesson-history-teacher-label"
                        label="선생님"
                        value={lessonHistoryTeacher}
                        onChange={(e) => setLessonHistoryTeacher(e.target.value)}
                      >
                        <MenuItem value="">전체</MenuItem>
                        {lessonHistoryTeacherOptions.map((t) => (
                          <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {lessonHistoryLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : lessonHistory.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 6, px: 2, textAlign: "center" }}>
                    선택한 조건에 받은 레슨이 없습니다.
                  </Typography>
                ) : (
                  <>
                    {(() => {
                      const nodes = [];
                      let lastMonth = null;
                      lessonHistory.forEach((r) => {
                        const d = dayjs(r.date);
                        const month = d.format("YYYY년 M월");
                        if (month !== lastMonth) {
                          lastMonth = month;
                          nodes.push(
                            <Box key={"grp-" + month} sx={{ px: 2, py: 0.75, bgcolor: "action.hover" }}>
                              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "text.secondary" }}>
                                {month} · {monthCount[month]}회
                              </Typography>
                            </Box>
                          );
                        }
                        nodes.push(
                          <Box
                            key={r.id}
                            sx={{ display: "flex", gap: 1.5, alignItems: "center", px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", "&:hover": { bgcolor: "action.hover" } }}
                          >
                            <Box sx={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: (t) => alpha(t.palette.info.main, 0.14), color: "info.main" }}>
                              <MusicNote fontSize="small" />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                  {d.format("MM.DD")} ({_KO_DAYS[d.day()]})
                                </Typography>
                                {r.is_rescheduled && (
                                  <Chip label="변경권" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: "0.62rem", "& .MuiChip-label": { px: 0.75 } }} />
                                )}
                              </Box>
                              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mt: 0.25 }}>
                                {String(r.start_time).slice(0, 5)} ~ {String(r.end_time).slice(0, 5)}
                              </Typography>
                            </Box>
                            <Chip
                              label={r.teacher?.name || "-"}
                              size="small"
                              variant="outlined"
                              sx={{ flexShrink: 0, height: 22, fontSize: "0.72rem" }}
                            />
                          </Box>
                        );
                      });
                      return nodes;
                    })()}

                    {lessonHistoryLoadingMore && (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 2 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">이전 레슨 불러오는 중</Typography>
                      </Box>
                    )}
                    {!lessonHistoryHasMore && !lessonHistoryLoadingMore && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: "block", textAlign: "center", py: 2.5 }}>
                        더 이상 레슨 기록이 없습니다
                      </Typography>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* 스케줄제 결제 처리 - 유효기간 입력 다이얼로그 */}
      <Dialog
        open={paymentDialog.open}
        onClose={() => setPaymentDialog({ open: false, memberId: null, memberName: "", lessonsPerWeek: 1 })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>결제 처리 - 유효기간 설정</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              <strong>{paymentDialog.memberName}</strong>님 (주 {paymentDialog.lessonsPerWeek}회)<br />
              발급 티켓: <strong>{paymentDialog.lessonsPerWeek === 2 ? 8 : 4}장</strong>
            </Alert>
            <DateField
              label="티켓 유효기간"
              value={paymentExpiresAt}
              onChange={setPaymentExpiresAt}
              fullWidth
              helperText="이 날짜까지 티켓을 사용할 수 있습니다."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog({ open: false, memberId: null, memberName: "", lessonsPerWeek: 1 })}>취소</Button>
          <Button onClick={handleProcessPaymentConfirm} variant="contained" disabled={loading || !paymentExpiresAt}>
            결제 처리
          </Button>
        </DialogActions>
      </Dialog>


      {/* 연습실 예약 제한 다이얼로그 */}
      <Dialog
        open={blockPracticeDialog.open}
        onClose={() => setBlockPracticeDialog({ open: false, memberId: null, memberName: "", hours: 24 })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          연습실 예약 제한
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              <strong>{blockPracticeDialog.memberName}</strong>님의 연습실 예약을 일정 시간 동안 제한합니다.
              <br />
              노쇼 등의 사유로 사용하세요.
            </Alert>
            <FormControl fullWidth>
              <InputLabel>제한 시간</InputLabel>
              <Select
                value={blockPracticeDialog.hours}
                label="제한 시간"
                onChange={(e) => setBlockPracticeDialog(prev => ({ ...prev, hours: e.target.value }))}
              >
                <MenuItem value={1}>1시간</MenuItem>
                <MenuItem value={3}>3시간</MenuItem>
                <MenuItem value={6}>6시간</MenuItem>
                <MenuItem value={12}>12시간</MenuItem>
                <MenuItem value={24}>24시간 (1일)</MenuItem>
                <MenuItem value={48}>48시간 (2일)</MenuItem>
                <MenuItem value={72}>72시간 (3일)</MenuItem>
                <MenuItem value={168}>168시간 (1주일)</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              제한 해제 예정: {new Date(Date.now() + blockPracticeDialog.hours * 60 * 60 * 1000).toLocaleString('ko-KR')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockPracticeDialog({ open: false, memberId: null, memberName: "", hours: 24 })}>
            취소
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBlockPractice}
            disabled={loading}
          >
            {loading ? "처리 중..." : "예약 제한"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 전역 스낵바(토스트) — window.alert 대체 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? 6000 : 3500}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", whiteSpace: "pre-line" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 전역 확인/안내 다이얼로그 — window.confirm 대체 */}
      <Dialog open={confirmState.open} onClose={() => closeConfirm(false)} maxWidth="xs" fullWidth>
        {confirmState.title && <DialogTitle sx={{ fontWeight: 600 }}>{confirmState.title}</DialogTitle>}
        <DialogContent>
          {confirmState.severity ? (
            <Alert severity={confirmState.severity} sx={{ whiteSpace: "pre-line" }}>{confirmState.message}</Alert>
          ) : (
            <Typography sx={{ whiteSpace: "pre-line" }}>{confirmState.message}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {!confirmState.hideCancel && <Button onClick={() => closeConfirm(false)}>{confirmState.cancelText}</Button>}
          <Button variant="contained" color={confirmState.color} onClick={() => closeConfirm(true)} autoFocus>
            {confirmState.confirmText}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 이미 사용된 보강 티켓 처리 결정 (스케줄 변경 시) */}
      <MakeupDecisionDialog
        open={makeupDecisionDialog.open}
        active={active}
        decision={makeupDecisionDialog.decision}
        submitting={makeupDecisionDialog.submitting}
        onCancel={() => {
          if (!makeupDecisionDialog.submitting) {
            setMakeupDecisionDialog({ open: false, decision: null, pending: null, submitting: false });
          }
        }}
        onConfirm={handleMakeupDecisionConfirm}
      />

      {/* 스케줄제 진입 시 남은 티켓 이월 결정 (등록·유형 전환) */}
      <ScheduleCarryoverDialog
        open={carryoverDialog.open}
        active={active}
        memberName={carryoverDialog.pending?.memberName}
        carryover={carryoverDialog.carryover}
        isUpdate={!!carryoverDialog.pending?.id}
        submitting={carryoverDialog.submitting}
        onCancel={() => {
          if (!carryoverDialog.submitting) {
            setCarryoverDialog({ open: false, carryover: null, pending: null, submitting: false });
          }
        }}
        onConfirm={handleCarryoverConfirm}
      />

      {/* 고정제 자동 예약 결과 (미배정/보강 티켓) — 플래너 다건 적용 시 회원별로 차례로 표시 */}
      <UnassignedResultDialog
        open={unassignedDialog.open && active}
        onClose={() => {
          setUnassignedDialog((prev) => {
            const queue = prev.queue || [];
            if (queue.length > 0) {
              return {
                open: true,
                ...queue[0],
                queue: queue.slice(1),
                seq: prev.seq ? { current: prev.seq.current + 1, total: prev.seq.total } : null,
              };
            }
            return { ...prev, open: false, queue: [], seq: null };
          });
        }}
        title={unassignedDialog.title}
        memberName={unassignedDialog.memberName}
        result={unassignedDialog.result}
        sequence={unassignedDialog.seq}
        teacherOptions={teacherOptions}
        onOpenMakeup={() => {
          const skips = unassignedDialog.result?.skips || [];
          const confirmed = skips.filter((s) => !s.is_provisional);
          const firstSkip = confirmed[0] || skips[0];
          // 큐는 유지한 채 닫기 — 보강 잡기 창이 닫히면 다음 회원 결과를 이어서 표시
          setUnassignedDialog((prev) => ({ ...prev, open: false }));
          setMakeupDialog({
            open: true,
            member: { id: unassignedDialog.memberId, name: unassignedDialog.memberName },
            initialDate: firstSkip?.date || null,
            marks: confirmed,
          });
        }}
      />

      {/* 보강 레슨 잡기 (주간 표에서 대리 예약) */}
      <MakeupBookingDialog
        open={makeupDialog.open}
        active={active}
        onClose={() => {
          setMakeupDialog((prev) => ({ ...prev, open: false }));
          // 결과 큐가 남아 있으면(플래너 다건 적용) 다음 회원 결과를 이어서 표시
          setUnassignedDialog((prev) => {
            const queue = prev.queue || [];
            if (!prev.open && queue.length > 0) {
              return {
                open: true,
                ...queue[0],
                queue: queue.slice(1),
                seq: prev.seq ? { current: prev.seq.current + 1, total: prev.seq.total } : null,
              };
            }
            return prev;
          });
        }}
        member={makeupDialog.member}
        initialDate={makeupDialog.initialDate}
        marks={makeupDialog.marks}
        onBooked={() => fetchAll()}
      />

    </Box>
  );
}

export default AdminPage;
